import { useState, useEffect, useRef } from "react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import {
  LayoutDashboard, Building2, Users, FileText, CalendarDays, DollarSign,
  Settings, Plus, Search, Bell, Eye, Pencil, Check, ArrowUpRight,
  ArrowDownRight, TrendingUp, Home, BedDouble, Bath, Maximize2, MapPin,
  MessageCircle, Send, Phone, Mail, Globe, Share2, Heart, Clock,
  AlertTriangle, Camera, Download, ChevronRight, ChevronLeft, Trash2,
  Instagram, Facebook, MessageSquare, Hash, CreditCard, Receipt, Activity, Key,
  Save, RefreshCw, Database, Server, Shield, Lock, Webhook, X, Copy,
  CheckCircle, AlertOctagon, Loader, Link, ExternalLink, Play, Pause
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   DATABASE SCHEMA & API LAYER
   All data persisted via window.storage
   ═══════════════════════════════════════════════════════════ */

const DB_SCHEMA = {
  companies: { pk: "id", fields: ["name","email","phone","address","logo","plan","status","created_at"] },
  users: { pk: "id", fields: ["company_id","first_name","last_name","email","phone","role","avatar","status","created_at"] },
  properties: { pk: "id", fields: ["company_id","name","type","status","purpose","address_country","address_city","address_district","address_full","address_zip","latitude","longitude","surface_m2","rooms","bedrooms","bathrooms","floor","has_elevator","has_parking","has_pool","has_garden","has_terrace","has_ac","has_heating","has_furnished","has_wifi","year_built","energy_class","description","youtube_url","our_cost","rent_price","sale_price","nightly_price","deposit_amount","charges_cleaning","charges_supplies","charges_laundry","charges_other","created_at","updated_at"] },
  property_media: { pk: "id", fields: ["property_id","url","type","is_cover","sort_order"] },
  clients: { pk: "id", fields: ["company_id","first_name","last_name","email","phone","whatsapp_id","telegram_id","facebook_id","instagram_handle","address","id_type","id_number","source","status","score","tags","notes","created_at"] },
  bookings: { pk: "id", fields: ["company_id","property_id","client_id","check_in","check_out","amount","status","payment_status","notes","created_at"] },
  contracts: { pk: "id", fields: ["company_id","property_id","client_id","type","start_date","end_date","monthly_rent","deposit","payment_day","auto_renew","status","signed_at","signature_method","created_at"] },
  payments: { pk: "id", fields: ["company_id","booking_id","contract_id","client_id","amount","currency","method","status","reference","paid_at","created_at"] },
  expenses: { pk: "id", fields: ["company_id","property_id","category","description","amount","currency","date","receipt_url","created_at"] },
  conversations: { pk: "id", fields: ["company_id","client_id","agent_id","channel","status","last_message_at"] },
  messages: { pk: "id", fields: ["conversation_id","sender_type","sender_id","content","channel","read","created_at"] },
  notifications: { pk: "id", fields: ["company_id","user_id","type","title","message","read","link","created_at"] },
  social_accounts: { pk: "id", fields: ["company_id","platform","account_name","account_id","access_token","refresh_token","status","connected_at"] },
  social_posts: { pk: "id", fields: ["company_id","property_id","platform","content","media_urls","status","scheduled_at","published_at","likes","comments","shares"] },
  maintenance_tickets: { pk: "id", fields: ["company_id","property_id","client_id","category","priority","title","description","status","assigned_to","cost","created_at","resolved_at"] },
  settings: { pk: "id", fields: ["company_id","key","value","category","updated_at"] },
};

const uuid = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
const now = () => new Date().toISOString();

// ─── API Layer ────────────────────────────────────────────
const API = {
  _cache: {},

  async getAll(table) {
    try {
      const result = await window.storage.get(`db:${table}`);
      return result ? JSON.parse(result.value) : [];
    } catch { return []; }
  },

  async save(table, records) {
    try {
      await window.storage.set(`db:${table}`, JSON.stringify(records));
      this._cache[table] = records;
      return true;
    } catch { return false; }
  },

  async insert(table, data) {
    const records = await this.getAll(table);
    const record = { id: uuid(), ...data, created_at: now() };
    records.push(record);
    await this.save(table, records);
    return record;
  },

  async update(table, id, data) {
    const records = await this.getAll(table);
    const idx = records.findIndex(r => r.id === id);
    if (idx === -1) return null;
    records[idx] = { ...records[idx], ...data, updated_at: now() };
    await this.save(table, records);
    return records[idx];
  },

  async remove(table, id) {
    const records = await this.getAll(table);
    const filtered = records.filter(r => r.id !== id);
    await this.save(table, filtered);
    return true;
  },

  async getSettings(companyId) {
    const all = await this.getAll("settings");
    return all.filter(s => s.company_id === companyId);
  },

  async setSetting(companyId, key, value, category) {
    const all = await this.getAll("settings");
    const idx = all.findIndex(s => s.company_id === companyId && s.key === key);
    if (idx >= 0) {
      all[idx] = { ...all[idx], value, updated_at: now() };
    } else {
      all.push({ id: uuid(), company_id: companyId, key, value, category, updated_at: now() });
    }
    await this.save("settings", all);
  },

  async getSetting(companyId, key) {
    const all = await this.getAll("settings");
    const s = all.find(s => s.company_id === companyId && s.key === key);
    return s ? s.value : "";
  },

  async getStats(companyId) {
    const [properties, clients, bookings, contracts, payments, expenses] = await Promise.all([
      this.getAll("properties"), this.getAll("clients"), this.getAll("bookings"),
      this.getAll("contracts"), this.getAll("payments"), this.getAll("expenses"),
    ]);
    const cp = properties.filter(p => p.company_id === companyId);
    const cc = clients.filter(c => c.company_id === companyId);
    const cb = bookings.filter(b => b.company_id === companyId);
    const ccon = contracts.filter(c => c.company_id === companyId);
    const cpay = payments.filter(p => p.company_id === companyId);
    const cexp = expenses.filter(e => e.company_id === companyId);
    const totalRev = cpay.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    const totalExp = cexp.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    return {
      totalProperties: cp.length,
      activeRentals: ccon.filter(c => c.status === "active").length,
      totalRevenue: totalRev,
      totalExpenses: totalExp,
      profit: totalRev - totalExp,
      totalClients: cc.length,
      totalBookings: cb.length,
      pendingBookings: cb.filter(b => b.status === "pending").length,
      confirmedBookings: cb.filter(b => b.status === "confirmed").length,
    };
  },
};

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════ */
const T = {
  bg: "#0B0F1A", surface: "#111827", surfaceAlt: "#1A2035", card: "#161D2F",
  border: "#1E2A45", borderLight: "#2A3A5C", text: "#E8ECF4", textMuted: "#7B8BA6",
  textDim: "#4A5568", primary: "#3B82F6", primaryGlow: "rgba(59,130,246,0.15)",
  accent: "#10B981", accentGlow: "rgba(16,185,129,0.15)", warning: "#F59E0B",
  danger: "#EF4444", purple: "#8B5CF6", pink: "#EC4899", cyan: "#06B6D4",
};

/* ═══════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════════════ */
const StatusBadge = ({ status }) => {
  const m = {
    rented:["rgba(16,185,129,0.15)","#10B981","Loué"], available:["rgba(59,130,246,0.15)","#3B82F6","Disponible"],
    maintenance:["rgba(245,158,11,0.15)","#F59E0B","Maintenance"], confirmed:["rgba(16,185,129,0.15)","#10B981","Confirmé"],
    pending:["rgba(245,158,11,0.15)","#F59E0B","En attente"], cancelled:["rgba(239,68,68,0.15)","#EF4444","Annulé"],
    active:["rgba(16,185,129,0.15)","#10B981","Actif"], draft:["rgba(107,114,128,0.15)","#7B8BA6","Brouillon"],
    expired:["rgba(239,68,68,0.15)","#EF4444","Expiré"], tenant:["rgba(16,185,129,0.15)","#10B981","Locataire"],
    lead:["rgba(59,130,246,0.15)","#3B82F6","Lead"], prospect:["rgba(245,158,11,0.15)","#F59E0B","Prospect"],
    published:["rgba(16,185,129,0.15)","#10B981","Publié"], scheduled:["rgba(139,92,246,0.15)","#8B5CF6","Planifié"],
    paid:["rgba(16,185,129,0.15)","#10B981","Payé"], unpaid:["rgba(239,68,68,0.15)","#EF4444","Impayé"],
    connected:["rgba(16,185,129,0.15)","#10B981","Connecté"], disconnected:["rgba(239,68,68,0.15)","#EF4444","Déconnecté"],
  };
  const s = m[status] || ["rgba(107,114,128,0.15)","#7B8BA6",status];
  return <span style={{ background:s[0], color:s[1], padding:"4px 10px", borderRadius:6, fontSize:12, fontWeight:600 }}>{s[2]}</span>;
};

const KPI = ({ icon:Icon, label, value, change, positive, color, glow }) => (
  <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:"20px 24px", position:"relative", overflow:"hidden", flex:1, minWidth:180 }}>
    <div style={{ position:"absolute", top:-20, right:-20, width:80, height:80, borderRadius:"50%", background:glow, filter:"blur(20px)" }} />
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
      <div style={{ background:glow, borderRadius:10, padding:8, display:"flex" }}><Icon size={20} color={color} /></div>
      {change && <div style={{ display:"flex", alignItems:"center", gap:2, fontSize:12, fontWeight:600, color:positive?T.accent:T.danger }}>
        {positive ? <ArrowUpRight size={14}/> : <ArrowDownRight size={14}/>}{change}
      </div>}
    </div>
    <div style={{ fontSize:26, fontWeight:700, color:T.text, letterSpacing:"-0.5px" }}>{value}</div>
    <div style={{ fontSize:13, color:T.textMuted, marginTop:4 }}>{label}</div>
  </div>
);

const Btn = ({ children, onClick, variant="primary", icon:Icon, style:s={} }) => {
  const styles = {
    primary: { background:T.primary, color:"#fff", border:"none" },
    secondary: { background:T.surfaceAlt, color:T.textMuted, border:`1px solid ${T.border}` },
    danger: { background:"rgba(239,68,68,0.15)", color:T.danger, border:`1px solid rgba(239,68,68,0.3)` },
    success: { background:"rgba(16,185,129,0.15)", color:T.accent, border:`1px solid rgba(16,185,129,0.3)` },
  };
  return (
    <button onClick={onClick} style={{ display:"flex", alignItems:"center", gap:6, borderRadius:10, padding:"9px 16px", fontSize:13, fontWeight:600, cursor:"pointer", ...styles[variant], ...s }}>
      {Icon && <Icon size={15}/>}{children}
    </button>
  );
};

const Input = ({ label, value, onChange, placeholder, type="text", textarea, icon:Icon }) => (
  <div style={{ marginBottom:14 }}>
    {label && <div style={{ fontSize:12, color:T.textMuted, marginBottom:5, fontWeight:500 }}>{label}</div>}
    <div style={{ position:"relative" }}>
      {Icon && <Icon size={15} color={T.textMuted} style={{ position:"absolute", left:12, top:textarea?12:11 }}/>}
      {textarea ? (
        <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={3}
          style={{ width:"100%", background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:10, padding:Icon?"10px 12px 10px 36px":"10px 12px", color:T.text, fontSize:13, outline:"none", resize:"vertical", fontFamily:"inherit", boxSizing:"border-box" }}/>
      ) : (
        <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
          style={{ width:"100%", background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:10, padding:Icon?"9px 12px 9px 36px":"9px 12px", color:T.text, fontSize:13, outline:"none", boxSizing:"border-box" }}/>
      )}
    </div>
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom:14 }}>
    {label && <div style={{ fontSize:12, color:T.textMuted, marginBottom:5, fontWeight:500 }}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{ width:"100%", background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:10, padding:"9px 12px", color:T.text, fontSize:13, outline:"none", boxSizing:"border-box", appearance:"auto" }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Toggle = ({ label, value, onChange, description }) => (
  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 0", borderBottom:`1px solid ${T.border}` }}>
    <div>
      <div style={{ fontSize:13, fontWeight:500, color:T.text }}>{label}</div>
      {description && <div style={{ fontSize:11, color:T.textDim, marginTop:2 }}>{description}</div>}
    </div>
    <div onClick={()=>onChange(!value)} style={{ width:44, height:24, borderRadius:12, background:value?T.accent:T.surfaceAlt, border:`1px solid ${value?T.accent:T.border}`, cursor:"pointer", position:"relative", transition:"all 0.2s" }}>
      <div style={{ width:18, height:18, borderRadius:9, background:"#fff", position:"absolute", top:2, left:value?22:3, transition:"left 0.2s" }}/>
    </div>
  </div>
);

const Modal = ({ open, onClose, title, children, width=560 }) => {
  if (!open) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:20, width, maxWidth:"90vw", maxHeight:"85vh", overflow:"auto", boxShadow:"0 24px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 24px", borderBottom:`1px solid ${T.border}` }}>
          <div style={{ fontSize:16, fontWeight:700, color:T.text }}>{title}</div>
          <button onClick={onClose} style={{ background:T.surfaceAlt, border:"none", borderRadius:8, padding:6, cursor:"pointer" }}><X size={16} color={T.textMuted}/></button>
        </div>
        <div style={{ padding:24 }}>{children}</div>
      </div>
    </div>
  );
};

const ChannelIcon = ({ channel, size=16 }) => {
  const m = { whatsapp:[MessageCircle,"#25D366"], telegram:[Send,"#0088cc"], email:[Mail,T.primary], facebook:[Facebook,"#1877F2"], instagram:[Instagram,"#E4405F"], tiktok:[Hash,"#FF0050"], sms:[Phone,T.warning] };
  const [I,c] = m[channel]||m.email;
  return <I size={size} color={c}/>;
};

const Toast = ({ message, type="success", onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, []);
  const colors = { success:[T.accent,T.accentGlow], error:[T.danger,"rgba(239,68,68,0.15)"], info:[T.primary,T.primaryGlow] };
  const [c,bg] = colors[type]||colors.info;
  return (
    <div style={{ position:"fixed", bottom:24, right:24, background:T.surface, border:`1px solid ${c}40`, borderRadius:14, padding:"12px 20px", display:"flex", alignItems:"center", gap:10, boxShadow:`0 8px 30px ${bg}`, zIndex:300, animation:"slideUp 0.3s ease" }}>
      {type==="success"?<CheckCircle size={18} color={c}/>:<AlertOctagon size={18} color={c}/>}
      <span style={{ fontSize:13, color:T.text }}>{message}</span>
    </div>
  );
};

const EmptyState = ({ icon:Icon, title, subtitle, action, onAction }) => (
  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"60px 20px", color:T.textMuted }}>
    <div style={{ background:T.surfaceAlt, borderRadius:16, padding:16, marginBottom:16 }}><Icon size={32} color={T.textDim}/></div>
    <div style={{ fontSize:16, fontWeight:600, color:T.text, marginBottom:6 }}>{title}</div>
    <div style={{ fontSize:13, marginBottom:20, textAlign:"center" }}>{subtitle}</div>
    {action && <Btn onClick={onAction} icon={Plus}>{action}</Btn>}
  </div>
);

const SectionCard = ({ title, description, children, actions }) => (
  <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, marginBottom:20, overflow:"hidden" }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 24px", borderBottom:`1px solid ${T.border}` }}>
      <div>
        <div style={{ fontSize:15, fontWeight:600, color:T.text }}>{title}</div>
        {description && <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>{description}</div>}
      </div>
      {actions && <div style={{ display:"flex", gap:8 }}>{actions}</div>}
    </div>
    <div style={{ padding:24 }}>{children}</div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   COMPANY CONTEXT
   ═══════════════════════════════════════════════════════════ */
const COMPANY_ID = "default_company";

/* ═══════════════════════════════════════════════════════════
   AUTH SYSTEM — Login, roles, session, security
   ═══════════════════════════════════════════════════════════ */
const AUTH = {
  async init() {
    // Create default admin if no users exist
    const users = await API.getAll("auth_users");
    if (users.length === 0) {
      const hash = await AUTH.hashPassword("admin2026");
      await API.insert("auth_users", {
        company_id: COMPANY_ID, email: "admin@hsimmo.tn", password_hash: hash,
        first_name: "Admin", last_name: "H&S", role: "super_admin",
        status: "active", avatar: "HS", login_attempts: 0, locked_until: null,
      });
    }
    // Create default session config
    const cfg = await API.getAll("auth_config");
    if (cfg.length === 0) {
      await API.insert("auth_config", {
        company_id: COMPANY_ID, max_login_attempts: 5, lockout_minutes: 15,
        session_hours: 24, require_2fa: false, password_min_length: 8,
        allowed_ips: "", maintenance_mode: false,
      });
    }
  },

  async hashPassword(pwd) {
    // Simple hash for demo — in production use bcrypt via API
    let h = 0;
    for (let i = 0; i < pwd.length; i++) {
      const c = pwd.charCodeAt(i);
      h = ((h << 5) - h) + c;
      h |= 0;
    }
    return "hs_" + Math.abs(h).toString(36) + "_" + btoa(pwd).replace(/=/g, "");
  },

  async verifyPassword(pwd, hash) {
    const check = await AUTH.hashPassword(pwd);
    return check === hash;
  },

  async login(email, password) {
    const users = await API.getAll("auth_users");
    const user = users.find(u => u.email === email.toLowerCase().trim());
    if (!user) return { ok: false, error: "Email ou mot de passe incorrect" };

    // Check lockout
    if (user.locked_until && new Date(user.locked_until) > new Date()) {
      const mins = Math.ceil((new Date(user.locked_until) - new Date()) / 60000);
      return { ok: false, error: `Compte verrouillé. Réessayez dans ${mins} minute(s).` };
    }

    // Check status
    if (user.status === "suspended") return { ok: false, error: "Compte suspendu. Contactez l'administrateur." };
    if (user.status === "inactive") return { ok: false, error: "Compte désactivé." };

    // Verify password
    const valid = await AUTH.verifyPassword(password, user.password_hash);
    if (!valid) {
      const attempts = (user.login_attempts || 0) + 1;
      const cfg = (await API.getAll("auth_config")).find(c => c.company_id === COMPANY_ID);
      const maxAttempts = cfg?.max_login_attempts || 5;
      const lockoutMins = cfg?.lockout_minutes || 15;

      if (attempts >= maxAttempts) {
        await API.update("auth_users", user.id, {
          login_attempts: attempts,
          locked_until: new Date(Date.now() + lockoutMins * 60000).toISOString(),
        });
        return { ok: false, error: `Trop de tentatives. Compte verrouillé pour ${lockoutMins} minutes.` };
      }
      await API.update("auth_users", user.id, { login_attempts: attempts });
      return { ok: false, error: `Email ou mot de passe incorrect (${maxAttempts - attempts} essai(s) restant(s))` };
    }

    // Success — reset attempts, log session
    await API.update("auth_users", user.id, { login_attempts: 0, locked_until: null, last_login: new Date().toISOString() });
    await API.insert("auth_sessions", {
      user_id: user.id, company_id: COMPANY_ID, ip: "browser",
      user_agent: navigator.userAgent.substring(0, 100),
      expires_at: new Date(Date.now() + 24 * 3600000).toISOString(),
    });
    // Audit log
    await API.insert("audit_logs", {
      company_id: COMPANY_ID, user_id: user.id, action: "login",
      details: `Connexion réussie : ${user.email}`, ip: "browser",
    });

    return { ok: true, user: { id: user.id, email: user.email, first_name: user.first_name, last_name: user.last_name, role: user.role, avatar: user.avatar } };
  },

  async logout(userId) {
    await API.insert("audit_logs", {
      company_id: COMPANY_ID, user_id: userId, action: "logout",
      details: "Déconnexion", ip: "browser",
    });
  },

  async getUsers() {
    return (await API.getAll("auth_users")).filter(u => u.company_id === COMPANY_ID);
  },

  async createUser(data) {
    const hash = await AUTH.hashPassword(data.password);
    return API.insert("auth_users", {
      ...data, password_hash: hash, company_id: COMPANY_ID,
      status: "active", login_attempts: 0, locked_until: null,
    });
  },

  async updateUser(id, data) {
    if (data.password) {
      data.password_hash = await AUTH.hashPassword(data.password);
      delete data.password;
    }
    return API.update("auth_users", id, data);
  },

  async getAuditLogs() {
    return (await API.getAll("audit_logs")).filter(l => l.company_id === COMPANY_ID).sort((a, b) => b.created_at?.localeCompare(a.created_at));
  },

  ROLES: {
    super_admin: { label: "Super Admin", color: "#C9A84C", pages: ["dashboard","properties","clients","bookings","contracts","finance","messages","social","settings","users"] },
    admin: { label: "Admin Agence", color: "#3B82F6", pages: ["dashboard","properties","clients","bookings","contracts","finance","messages","social","settings"] },
    agent: { label: "Agent", color: "#10B981", pages: ["dashboard","properties","clients","bookings","contracts","messages"] },
    accountant: { label: "Comptable", color: "#8B5CF6", pages: ["dashboard","finance"] },
    viewer: { label: "Lecteur", color: "#7B8BA6", pages: ["dashboard","properties"] },
  },
};

/* ═══════════════════════════════════════════════════════════
   PAGE: DASHBOARD
   ═══════════════════════════════════════════════════════════ */
const DashboardPage = ({ setPage }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const s = await API.getStats(COMPANY_ID);
      setStats(s);
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:300, color:T.textMuted }}><Loader size={24} style={{ animation:"spin 1s linear infinite" }}/></div>;
  if (!stats) return null;

  const fmt = (n) => n.toLocaleString("fr-FR");

  return (
    <div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:14, marginBottom:24 }}>
        <KPI icon={Building2} label="Total Propriétés" value={fmt(stats.totalProperties)} color={T.primary} glow={T.primaryGlow}/>
        <KPI icon={Key} label="Locations Actives" value={fmt(stats.activeRentals)} color={T.accent} glow={T.accentGlow}/>
        <KPI icon={DollarSign} label="Revenus" value={`${fmt(stats.totalRevenue)} TND`} color={T.accent} glow={T.accentGlow}/>
        <KPI icon={Receipt} label="Dépenses" value={`${fmt(stats.totalExpenses)} TND`} color={T.warning} glow="rgba(245,158,11,0.15)"/>
        <KPI icon={TrendingUp} label="Profit Net" value={`${fmt(stats.profit)} TND`} color={T.purple} glow="rgba(139,92,246,0.15)"/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:20 }}>
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:20, textAlign:"center" }}>
          <div style={{ fontSize:32, fontWeight:700, color:T.primary }}>{stats.totalClients}</div>
          <div style={{ fontSize:13, color:T.textMuted }}>Clients</div>
        </div>
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:20, textAlign:"center" }}>
          <div style={{ fontSize:32, fontWeight:700, color:T.accent }}>{stats.confirmedBookings}</div>
          <div style={{ fontSize:13, color:T.textMuted }}>Réservations Confirmées</div>
        </div>
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:20, textAlign:"center" }}>
          <div style={{ fontSize:32, fontWeight:700, color:T.warning }}>{stats.pendingBookings}</div>
          <div style={{ fontSize:13, color:T.textMuted }}>En Attente</div>
        </div>
      </div>

      {stats.totalProperties === 0 && (
        <EmptyState icon={Building2} title="Aucune propriété" subtitle="Commencez par ajouter vos biens immobiliers" action="Ajouter une propriété" onAction={() => setPage && setPage("properties")}/>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE: PROPERTIES
   ═══════════════════════════════════════════════════════════ */
const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const all = await API.getAll("properties");
    setProperties(all.filter(p => p.company_id === COMPANY_ID));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => setForm({ name:"",type:"apartment",status:"available",purpose:"rent",address_city:"",address_district:"",address_country:"Tunisie",address_full:"",our_cost:"",rent_price:"",nightly_price:"",deposit_amount:"",surface_m2:"",rooms:"",bedrooms:"",bathrooms:"",description:"",charges_cleaning:"",charges_supplies:"",charges_laundry:"",charges_other:"",has_parking:false,has_pool:false,has_ac:false,has_wifi:false,has_furnished:false });

  const openNew = () => { resetForm(); setEditId(null); setModal(true); };
  const openEdit = (p) => { setForm(p); setEditId(p.id); setModal(true); };

  const handleSave = async () => {
    if (!form.name) return;
    if (editId) {
      await API.update("properties", editId, form);
    } else {
      await API.insert("properties", { ...form, company_id: COMPANY_ID });
    }
    setModal(false);
    load();
  };

  const handleDelete = async (id) => {
    await API.remove("properties", id);
    load();
  };

  const filtered = properties.filter(p => {
    if (filter !== "all" && p.status !== filter) return false;
    if (search && !p.name?.toLowerCase().includes(search.toLowerCase()) && !p.address_city?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const typeLabels = { apartment:"Appartement", villa:"Villa", house:"Maison", studio:"Studio", commercial:"Commercial", land:"Terrain" };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <div style={{ position:"relative" }}>
            <Search size={16} color={T.textMuted} style={{ position:"absolute", left:12, top:10 }}/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher..."
              style={{ background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:10, padding:"9px 12px 9px 36px", color:T.text, fontSize:13, width:240, outline:"none" }}/>
          </div>
          <div style={{ display:"flex", gap:3, background:T.surfaceAlt, borderRadius:10, padding:3, border:`1px solid ${T.border}` }}>
            {[["all","Tous"],["available","Disponible"],["rented","Loué"],["maintenance","Maintenance"]].map(([v,l]) => (
              <button key={v} onClick={()=>setFilter(v)} style={{ padding:"6px 12px", borderRadius:8, fontSize:12, fontWeight:500, border:"none", cursor:"pointer", background:filter===v?T.primary:"transparent", color:filter===v?"#fff":T.textMuted }}>{l}</button>
            ))}
          </div>
        </div>
        <Btn onClick={openNew} icon={Plus}>Ajouter</Btn>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Building2} title="Aucune propriété" subtitle="Ajoutez votre premier bien immobilier" action="Ajouter" onAction={openNew}/>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:16 }}>
          {filtered.map(p => (
            <div key={p.id} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, overflow:"hidden" }}>
              <div style={{ height:120, background:`linear-gradient(135deg, ${T.surfaceAlt}, ${T.surface})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:36, position:"relative" }}>
                <Building2 size={36} color={T.textDim}/>
                <div style={{ position:"absolute", top:10, right:10 }}><StatusBadge status={p.status}/></div>
                <div style={{ position:"absolute", top:10, left:10, background:"rgba(0,0,0,0.5)", borderRadius:6, padding:"3px 8px", fontSize:11, color:"#fff", fontWeight:600 }}>{typeLabels[p.type]||p.type}</div>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <div>
                    <div style={{ fontSize:15, fontWeight:600, color:T.text }}>{p.name}</div>
                    <div style={{ fontSize:12, color:T.textMuted, display:"flex", alignItems:"center", gap:4, marginTop:2 }}><MapPin size={12}/>{p.address_district}{p.address_city?`, ${p.address_city}`:""}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:16, fontWeight:700, color:T.accent }}>{parseFloat(p.rent_price||0).toLocaleString()} <span style={{ fontSize:10, fontWeight:400, color:T.textMuted }}>TND loc.</span></div>
                  {p.our_cost && <div style={{ fontSize:11, color:T.warning }}>{parseFloat(p.our_cost||0).toLocaleString()} TND coût</div>}
                  {p.our_cost && p.rent_price && <div style={{ fontSize:10, fontWeight:600, color:parseFloat(p.rent_price)-parseFloat(p.our_cost)>0?T.accent:T.danger }}>Marge: {(parseFloat(p.rent_price)-parseFloat(p.our_cost)).toLocaleString()} TND</div>}
                </div>
                </div>
                <div style={{ display:"flex", gap:14, marginTop:12, paddingTop:12, borderTop:`1px solid ${T.border}` }}>
                  {p.bedrooms && <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, color:T.textMuted }}><BedDouble size={14}/>{p.bedrooms}</div>}
                  {p.bathrooms && <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, color:T.textMuted }}><Bath size={14}/>{p.bathrooms}</div>}
                  {p.surface_m2 && <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, color:T.textMuted }}><Maximize2 size={14}/>{p.surface_m2}m²</div>}
                  <div style={{ marginLeft:"auto", display:"flex", gap:4 }}>
                    <button onClick={()=>openEdit(p)} style={{ background:T.surfaceAlt, border:"none", borderRadius:6, padding:6, cursor:"pointer" }}><Pencil size={13} color={T.textMuted}/></button>
                    <button onClick={()=>handleDelete(p.id)} style={{ background:T.surfaceAlt, border:"none", borderRadius:6, padding:6, cursor:"pointer" }}><Trash2 size={13} color={T.danger}/></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={()=>setModal(false)} title={editId?"Modifier Propriété":"Nouvelle Propriété"} width={640}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
          <Input label="Nom" value={form.name||""} onChange={v=>setForm({...form,name:v})} placeholder="Villa Carthage"/>
          <Select label="Type" value={form.type||"apartment"} onChange={v=>setForm({...form,type:v})} options={[{value:"apartment",label:"Appartement"},{value:"villa",label:"Villa"},{value:"house",label:"Maison"},{value:"studio",label:"Studio"},{value:"commercial",label:"Commercial"},{value:"land",label:"Terrain"}]}/>
          <Select label="Statut" value={form.status||"available"} onChange={v=>setForm({...form,status:v})} options={[{value:"available",label:"Disponible"},{value:"rented",label:"Loué"},{value:"maintenance",label:"Maintenance"}]}/>
          <Select label="Objectif" value={form.purpose||"rent"} onChange={v=>setForm({...form,purpose:v})} options={[{value:"rent",label:"Location"},{value:"sale",label:"Vente"},{value:"sublease",label:"Sous-location"},{value:"short_term",label:"Court terme"}]}/>
          <Input label="Ville" value={form.address_city||""} onChange={v=>setForm({...form,address_city:v})} placeholder="Tunis"/>
          <Input label="Quartier" value={form.address_district||""} onChange={v=>setForm({...form,address_district:v})} placeholder="Les Berges du Lac"/>
          <Input label="Loyer qu'on paye (coût TND)" value={form.our_cost||""} onChange={v=>setForm({...form,our_cost:v})} type="number" placeholder="2000"/>
          <Input label="Prix location (TND/mois)" value={form.rent_price||""} onChange={v=>setForm({...form,rent_price:v})} type="number" placeholder="3500"/>
          <Input label="Prix par nuit (TND)" value={form.nightly_price||""} onChange={v=>setForm({...form,nightly_price:v})} type="number" placeholder="150"/>
          <Input label="Caution (TND)" value={form.deposit_amount||""} onChange={v=>setForm({...form,deposit_amount:v})} type="number" placeholder="500"/>
          <Input label="Surface (m²)" value={form.surface_m2||""} onChange={v=>setForm({...form,surface_m2:v})} type="number" placeholder="120"/>
          <Input label="Chambres" value={form.bedrooms||""} onChange={v=>setForm({...form,bedrooms:v})} type="number" placeholder="3"/>
          <Input label="Salles de bain" value={form.bathrooms||""} onChange={v=>setForm({...form,bathrooms:v})} type="number" placeholder="2"/>
          <Input label="Pièces" value={form.rooms||""} onChange={v=>setForm({...form,rooms:v})} type="number" placeholder="5"/>
        </div>
        <div style={{ marginTop:8, marginBottom:12, padding:"12px 16px", background:T.surfaceAlt, borderRadius:12, border:`1px solid ${T.border}` }}>
          <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:10 }}>Charges récurrentes (TND/séjour)</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <Input label="Ménage / Femme de ménage" value={form.charges_cleaning||""} onChange={v=>setForm({...form,charges_cleaning:v})} type="number" placeholder="50"/>
            <Input label="Fournitures (javel, papier, etc.)" value={form.charges_supplies||""} onChange={v=>setForm({...form,charges_supplies:v})} type="number" placeholder="20"/>
            <Input label="Linge / Blanchisserie" value={form.charges_laundry||""} onChange={v=>setForm({...form,charges_laundry:v})} type="number" placeholder="30"/>
            <Input label="Autres charges" value={form.charges_other||""} onChange={v=>setForm({...form,charges_other:v})} type="number" placeholder="0"/>
          </div>
          {(form.charges_cleaning || form.charges_supplies || form.charges_laundry || form.charges_other) && (
            <div style={{ marginTop:8, fontSize:12, color:T.warning, fontWeight:600 }}>
              Total charges/séjour : {([form.charges_cleaning,form.charges_supplies,form.charges_laundry,form.charges_other].reduce((s,v)=>s+(parseFloat(v)||0),0)).toLocaleString()} TND
            </div>
          )}
        </div>
        <Input label="Description" value={form.description||""} onChange={v=>setForm({...form,description:v})} textarea placeholder="Description du bien..."/>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:10 }}>
          <Btn variant="secondary" onClick={()=>setModal(false)}>Annuler</Btn>
          <Btn onClick={handleSave} icon={Save}>{editId?"Modifier":"Créer"}</Btn>
        </div>
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE: CLIENTS
   ═══════════════════════════════════════════════════════════ */
const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);

  const load = async () => { setClients((await API.getAll("clients")).filter(c=>c.company_id===COMPANY_ID)); setLoading(false); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ first_name:"",last_name:"",email:"",phone:"",source:"website",status:"lead",score:50 }); setEditId(null); setModal(true); };
  const openEdit = (c) => { setForm(c); setEditId(c.id); setModal(true); };

  const handleSave = async () => {
    if (!form.first_name) return;
    if (editId) await API.update("clients",editId,form);
    else await API.insert("clients",{...form,company_id:COMPANY_ID});
    setModal(false); load();
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
        <div style={{ fontSize:13, color:T.textMuted }}>{clients.length} client(s)</div>
        <Btn onClick={openNew} icon={Plus}>Nouveau Client</Btn>
      </div>

      {clients.length === 0 ? (
        <EmptyState icon={Users} title="Aucun client" subtitle="Ajoutez votre premier client ou lead" action="Ajouter" onAction={openNew}/>
      ) : (
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                {["Client","Téléphone","Source","Statut","Score","Actions"].map(h=>(
                  <th key={h} style={{ padding:"12px 16px", textAlign:"left", fontSize:12, fontWeight:600, color:T.textMuted, textTransform:"uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map(c=>(
                <tr key={c.id} style={{ borderBottom:`1px solid ${T.border}` }}>
                  <td style={{ padding:"12px 16px" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{c.first_name} {c.last_name}</div>
                    <div style={{ fontSize:11, color:T.textMuted }}>{c.email}</div>
                  </td>
                  <td style={{ padding:"12px 16px", fontSize:13, color:T.textMuted }}>{c.phone}</td>
                  <td style={{ padding:"12px 16px" }}><div style={{ display:"flex", alignItems:"center", gap:6 }}><ChannelIcon channel={c.source} size={14}/><span style={{ fontSize:12, color:T.textMuted }}>{c.source}</span></div></td>
                  <td style={{ padding:"12px 16px" }}><StatusBadge status={c.status}/></td>
                  <td style={{ padding:"12px 16px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:50, height:5, background:T.surfaceAlt, borderRadius:3 }}><div style={{ height:"100%", width:`${c.score||0}%`, borderRadius:3, background:(c.score||0)>70?T.accent:(c.score||0)>40?T.warning:T.danger }}/></div>
                      <span style={{ fontSize:12, fontWeight:600, color:(c.score||0)>70?T.accent:(c.score||0)>40?T.warning:T.danger }}>{c.score||0}</span>
                    </div>
                  </td>
                  <td style={{ padding:"12px 16px" }}>
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={()=>openEdit(c)} style={{ background:T.surfaceAlt, border:"none", borderRadius:6, padding:6, cursor:"pointer" }}><Pencil size={14} color={T.textMuted}/></button>
                      <button onClick={async()=>{ await API.remove("clients",c.id); load(); }} style={{ background:T.surfaceAlt, border:"none", borderRadius:6, padding:6, cursor:"pointer" }}><Trash2 size={14} color={T.danger}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modal} onClose={()=>setModal(false)} title={editId?"Modifier Client":"Nouveau Client"}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
          <Input label="Prénom" value={form.first_name||""} onChange={v=>setForm({...form,first_name:v})}/>
          <Input label="Nom" value={form.last_name||""} onChange={v=>setForm({...form,last_name:v})}/>
          <Input label="Email" value={form.email||""} onChange={v=>setForm({...form,email:v})} type="email"/>
          <Input label="Téléphone" value={form.phone||""} onChange={v=>setForm({...form,phone:v})}/>
          <Select label="Source" value={form.source||"website"} onChange={v=>setForm({...form,source:v})} options={[{value:"website",label:"Site Web"},{value:"whatsapp",label:"WhatsApp"},{value:"facebook",label:"Facebook"},{value:"instagram",label:"Instagram"},{value:"tiktok",label:"TikTok"},{value:"telegram",label:"Telegram"},{value:"referral",label:"Référence"}]}/>
          <Select label="Statut" value={form.status||"lead"} onChange={v=>setForm({...form,status:v})} options={[{value:"lead",label:"Lead"},{value:"prospect",label:"Prospect"},{value:"tenant",label:"Locataire"}]}/>
          <Input label="Score" value={form.score||""} onChange={v=>setForm({...form,score:v})} type="number"/>
          <Input label="WhatsApp ID" value={form.whatsapp_id||""} onChange={v=>setForm({...form,whatsapp_id:v})}/>
        </div>
        <Input label="Notes" value={form.notes||""} onChange={v=>setForm({...form,notes:v})} textarea/>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:10 }}>
          <Btn variant="secondary" onClick={()=>setModal(false)}>Annuler</Btn>
          <Btn onClick={handleSave} icon={Save}>{editId?"Modifier":"Créer"}</Btn>
        </div>
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE: BOOKINGS — Real-time calendar, auto availability sync
   Check-in: 14h00 / Check-out: 12h00
   ═══════════════════════════════════════════════════════════ */
const BookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [view, setView] = useState("list");
  const [calMonth, setCalMonth] = useState(new Date());
  const [toast, setToast] = useState(null);

  const load = async () => {
    const [b, p, c] = await Promise.all([API.getAll("bookings"), API.getAll("properties"), API.getAll("clients")]);
    setBookings(b.filter(x => x.company_id === COMPANY_ID));
    setProperties(p.filter(x => x.company_id === COMPANY_ID));
    setClients(c.filter(x => x.company_id === COMPANY_ID));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Auto-sync property status based on bookings
  const syncPropertyStatus = async (propertyId) => {
    const today = new Date().toISOString().split("T")[0];
    const allBookings = (await API.getAll("bookings")).filter(b => b.company_id === COMPANY_ID && b.property_id === propertyId && b.status === "confirmed");
    const isOccupied = allBookings.some(b => b.check_in <= today && b.check_out > today);
    const prop = properties.find(p => p.id === propertyId);
    if (prop && prop.status !== "maintenance") {
      await API.update("properties", propertyId, { status: isOccupied ? "rented" : "available" });
    }
  };

  const isDateBooked = (propertyId, date) => {
    const d = date.toISOString().split("T")[0];
    return bookings.some(b => b.property_id === propertyId && b.status !== "cancelled" && b.check_in <= d && b.check_out > d);
  };

  const getDuration = (cin, cout) => {
    if (!cin || !cout) return 0;
    return Math.ceil((new Date(cout) - new Date(cin)) / (1000 * 60 * 60 * 24));
  };

  const getDurationLabel = (days) => {
    if (days <= 0) return "";
    if (days === 1) return "1 nuit";
    if (days < 7) return `${days} nuits`;
    if (days === 7) return "1 semaine";
    if (days < 30) return `${days} nuits (${Math.floor(days/7)} sem.)`;
    return `${days} nuits (${Math.floor(days/30)} mois)`;
  };

  const getName = (list, id, fields) => { const r = list.find(i => i.id === id); return r ? fields.map(f => r[f]).filter(Boolean).join(" ") : "—"; };
  const getProperty = (id) => properties.find(p => p.id === id);

  const handleSave = async () => {
    if (!form.property_id || !form.client_id || !form.check_in || !form.check_out) {
      setToast({ message: "Veuillez remplir tous les champs obligatoires", type: "error" });
      return;
    }
    // Check date conflict
    const conflict = bookings.some(b => b.property_id === form.property_id && b.status !== "cancelled" &&
      ((form.check_in >= b.check_in && form.check_in < b.check_out) || (form.check_out > b.check_in && form.check_out <= b.check_out) || (form.check_in <= b.check_in && form.check_out >= b.check_out))
    );
    if (conflict) { setToast({ message: "Conflit de dates ! Ce bien est déjà réservé sur cette période.", type: "error" }); return; }

    const dur = getDuration(form.check_in, form.check_out);
    const prop = getProperty(form.property_id);
    const nightlyRate = parseFloat(prop?.nightly_price || prop?.rent_price || 0);
    const autoAmount = form.amount || (nightlyRate * dur);

    await API.insert("bookings", {
      ...form, company_id: COMPANY_ID, status: "pending", payment_status: "unpaid",
      amount: autoAmount, duration_nights: dur, check_in_time: "14:00", check_out_time: "12:00",
    });
    await syncPropertyStatus(form.property_id);
    setModal(false); setToast({ message: "Réservation créée", type: "success" }); load();
  };

  const confirmBooking = async (b) => {
    await API.update("bookings", b.id, { status: "confirmed" });
    await syncPropertyStatus(b.property_id);
    setToast({ message: "Réservation confirmée", type: "success" }); load();
  };

  const cancelBooking = async (b) => {
    await API.update("bookings", b.id, { status: "cancelled" });
    await syncPropertyStatus(b.property_id);
    setToast({ message: "Réservation annulée", type: "info" }); load();
  };

  // Calendar renderer
  const renderCalendar = () => {
    const year = calMonth.getFullYear(), month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date().toISOString().split("T")[0];
    const days = [];
    for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));

    return (
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <button onClick={() => setCalMonth(new Date(year, month - 1))} style={{ background: T.surfaceAlt, border: "none", borderRadius: 8, padding: 8, cursor: "pointer", color: T.textMuted }}><ChevronLeft size={16} /></button>
          <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{calMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
          <button onClick={() => setCalMonth(new Date(year, month + 1))} style={{ background: T.surfaceAlt, border: "none", borderRadius: 8, padding: 8, cursor: "pointer", color: T.textMuted }}><ChevronRight size={16} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(d => (
            <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: T.textDim, padding: 6 }}>{d}</div>
          ))}
        </div>
        {properties.length > 0 && properties.slice(0, 5).map(prop => (
          <div key={prop.id} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.primary, marginBottom: 4, padding: "4px 0" }}>{prop.name}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
              {days.map((day, i) => {
                if (!day) return <div key={i} />;
                const ds = day.toISOString().split("T")[0];
                const booked = isDateBooked(prop.id, day);
                const isPast = ds < today;
                const booking = bookings.find(b => b.property_id === prop.id && b.status !== "cancelled" && b.check_in <= ds && b.check_out > ds);
                const isCheckIn = booking && booking.check_in === ds;
                const isCheckOut = booking && new Date(new Date(booking.check_out).getTime() - 86400000).toISOString().split("T")[0] === ds;
                return (
                  <div key={i} style={{
                    textAlign: "center", padding: "4px 2px", fontSize: 11, borderRadius: 4, cursor: booked ? "default" : "pointer",
                    background: booked ? (booking?.status === "confirmed" ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)") : isPast ? T.surfaceAlt : "transparent",
                    color: booked ? (booking?.status === "confirmed" ? T.accent : T.warning) : isPast ? T.textDim : T.text,
                    border: isCheckIn ? `1px solid ${T.accent}` : isCheckOut ? `1px solid ${T.warning}` : `1px solid transparent`,
                  }} title={booked ? `${getName(clients, booking?.client_id, ["first_name", "last_name"])} — ${booking?.status}` : "Disponible"}>
                    {day.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 16, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textMuted }}><div style={{ width: 12, height: 12, borderRadius: 3, background: "rgba(16,185,129,0.2)" }} /> Confirmé</div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textMuted }}><div style={{ width: 12, height: 12, borderRadius: 3, background: "rgba(245,158,11,0.2)" }} /> En attente</div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textMuted }}><div style={{ width: 12, height: 12, borderRadius: 3, background: "transparent", border: `1px solid ${T.border}` }} /> Disponible</div>
          <div style={{ marginLeft: "auto", fontSize: 11, color: T.textMuted }}>Check-in: <strong style={{ color: T.accent }}>14h00</strong> — Check-out: <strong style={{ color: T.warning }}>12h00</strong></div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <KPI icon={CalendarDays} label="Total" value={bookings.length.toString()} color={T.primary} glow={T.primaryGlow} />
          <KPI icon={Check} label="Confirmées" value={bookings.filter(b => b.status === "confirmed").length.toString()} color={T.accent} glow={T.accentGlow} />
          <KPI icon={Clock} label="En attente" value={bookings.filter(b => b.status === "pending").length.toString()} color={T.warning} glow="rgba(245,158,11,0.15)" />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant={view === "list" ? "primary" : "secondary"} onClick={() => setView("list")}>Liste</Btn>
          <Btn variant={view === "calendar" ? "primary" : "secondary"} onClick={() => setView("calendar")} icon={CalendarDays}>Calendrier</Btn>
          <Btn onClick={() => { setForm({ property_id: "", client_id: "", check_in: "", check_out: "", amount: "", notes: "" }); setModal(true); }} icon={Plus}>Réservation</Btn>
        </div>
      </div>

      {view === "calendar" ? renderCalendar() : (
        bookings.length === 0 ? (
          <EmptyState icon={CalendarDays} title="Aucune réservation" subtitle="Les réservations apparaîtront ici" />
        ) : (
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Client", "Bien", "Check-in 14h", "Check-out 12h", "Durée", "Montant", "Statut", "Actions"].map(h => (<th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: "uppercase" }}>{h}</th>))}
              </tr></thead>
              <tbody>
                {bookings.sort((a, b) => (b.check_in || "").localeCompare(a.check_in || "")).map(b => {
                  const dur = getDuration(b.check_in, b.check_out);
                  return (
                    <tr key={b.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 500, color: T.text }}>{getName(clients, b.client_id, ["first_name", "last_name"])}</td>
                      <td style={{ padding: "12px 14px", fontSize: 13, color: T.textMuted }}>{getName(properties, b.property_id, ["name"])}</td>
                      <td style={{ padding: "12px 14px", fontSize: 13, color: T.text }}>{b.check_in} <span style={{ fontSize: 10, color: T.accent }}>14h</span></td>
                      <td style={{ padding: "12px 14px", fontSize: 13, color: T.text }}>{b.check_out} <span style={{ fontSize: 10, color: T.warning }}>12h</span></td>
                      <td style={{ padding: "12px 14px", fontSize: 12, color: T.textMuted }}>{getDurationLabel(dur)}</td>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: T.text }}>{parseFloat(b.amount || 0).toLocaleString()} TND</td>
                      <td style={{ padding: "12px 14px" }}><StatusBadge status={b.status} /></td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          {b.status === "pending" && <button onClick={() => confirmBooking(b)} style={{ background: T.accentGlow, border: "none", borderRadius: 6, padding: 6, cursor: "pointer" }} title="Confirmer"><Check size={14} color={T.accent} /></button>}
                          {b.status !== "cancelled" && <button onClick={() => cancelBooking(b)} style={{ background: "rgba(239,68,68,0.1)", border: "none", borderRadius: 6, padding: 6, cursor: "pointer" }} title="Annuler"><X size={14} color={T.danger} /></button>}
                          <button onClick={async () => { await API.remove("bookings", b.id); load(); }} style={{ background: T.surfaceAlt, border: "none", borderRadius: 6, padding: 6, cursor: "pointer" }} title="Supprimer"><Trash2 size={14} color={T.danger} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Nouvelle Réservation" width={600}>
        <div style={{ background: T.primaryGlow, borderRadius: 12, padding: "10px 16px", marginBottom: 16, fontSize: 12, color: T.text }}>
          <strong>Horaires :</strong> Check-in à <strong style={{ color: T.accent }}>14h00</strong> — Check-out à <strong style={{ color: T.warning }}>12h00</strong>. Calcul automatique du montant selon la durée.
        </div>
        <Select label="Propriété *" value={form.property_id || ""} onChange={v => setForm({ ...form, property_id: v })} options={[{ value: "", label: "Sélectionner..." }, ...properties.filter(p => p.status !== "maintenance").map(p => ({ value: p.id, label: `${p.name} — ${parseFloat(p.nightly_price || p.rent_price || 0)} TND/nuit` }))]} />
        <Select label="Client *" value={form.client_id || ""} onChange={v => setForm({ ...form, client_id: v })} options={[{ value: "", label: "Sélectionner..." }, ...clients.map(c => ({ value: c.id, label: `${c.first_name} ${c.last_name}` }))]} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Input label="Date arrivée (14h00) *" value={form.check_in || ""} onChange={v => setForm({ ...form, check_in: v })} type="date" />
          <Input label="Date départ (12h00) *" value={form.check_out || ""} onChange={v => setForm({ ...form, check_out: v })} type="date" />
        </div>
        {form.check_in && form.check_out && (() => {
          const dur = getDuration(form.check_in, form.check_out);
          const prop = getProperty(form.property_id);
          const rate = parseFloat(prop?.nightly_price || prop?.rent_price || 0);
          return dur > 0 ? (
            <div style={{ background: T.surfaceAlt, borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: T.textMuted }}>{getDurationLabel(dur)} x {rate.toLocaleString()} TND</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: T.primary }}>{(rate * dur).toLocaleString()} TND</span>
            </div>
          ) : null;
        })()}
        <Input label="Montant personnalisé (TND) — laisser vide pour calcul auto" value={form.amount || ""} onChange={v => setForm({ ...form, amount: v })} type="number" />
        <Input label="Notes" value={form.notes || ""} onChange={v => setForm({ ...form, notes: v })} textarea placeholder="Instructions spéciales..." />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
          <Btn variant="secondary" onClick={() => setModal(false)}>Annuler</Btn>
          <Btn onClick={handleSave} icon={Save}>Créer Réservation</Btn>
        </div>
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE: CONTRACTS — Full legal system with online signature,
   auto-clauses, caution, rules, duration-based contracts
   ═══════════════════════════════════════════════════════════ */

// Legal clauses generator based on contract type and duration
const generateClauses = (contract, property, client, agencyName) => {
  const pName = property?.name || "le bien";
  const cName = `${client?.first_name || ""} ${client?.last_name || ""}`.trim() || "le locataire";
  const dur = contract.duration_nights || 0;
  const deposit = parseFloat(contract.deposit || 0);
  const rent = parseFloat(contract.monthly_rent || contract.total_amount || 0);
  const addr = [property?.address_district, property?.address_city].filter(Boolean).join(", ") || "adresse du bien";

  const clauses = [
    {
      title: "Article 1 — Objet du contrat",
      text: `Le bailleur, représenté par ${agencyName || "l'agence"}, met à disposition du preneur, ${cName}, le logement situé à : ${addr}, désigné "${pName}", pour une durée de ${dur} nuit(s), du ${contract.start_date || "—"} (entrée à 14h00) au ${contract.end_date || "—"} (sortie à 12h00).`
    },
    {
      title: "Article 2 — Tarif et paiement",
      text: `Le montant total de la location est fixé à ${rent.toLocaleString()} TND, payable intégralement avant l'entrée dans les lieux. Tout retard de paiement entraîne la suspension de l'accès au logement. Les modes de paiement acceptés sont : carte bancaire, virement, espèces ou paiement mobile.`
    },
    {
      title: "Article 3 — Caution / Dépôt de garantie",
      text: deposit > 0
        ? `Une caution de ${deposit.toLocaleString()} TND est exigée à l'entrée. Cette somme sera restituée intégralement dans un délai de 7 jours après le départ, sous réserve de l'état des lieux de sortie. En cas de dégradation, casse, perte de clés ou d'équipements, le montant des réparations ou remplacements sera déduit de la caution. Si le montant des dommages excède la caution, le preneur s'engage à régler la différence.`
        : `Aucune caution n'est exigée pour cette réservation. Le preneur reste néanmoins responsable de tout dommage causé au logement et à son contenu.`
    },
    {
      title: "Article 4 — État des lieux",
      text: `Un état des lieux d'entrée sera effectué le jour de l'arrivée (14h00) et un état des lieux de sortie le jour du départ (12h00). Le preneur dispose de 2 heures après son arrivée pour signaler tout défaut préexistant. Passé ce délai, le logement est considéré comme accepté en l'état. Des photos horodatées seront prises à l'entrée et à la sortie comme preuve.`
    },
    {
      title: "Article 5 — Propreté et entretien",
      text: `Le preneur s'engage à maintenir le logement dans un état de propreté convenable pendant toute la durée du séjour. Le logement doit être restitué propre et rangé, dans l'état où il a été trouvé à l'arrivée. La vaisselle doit être lavée, les poubelles vidées, et les équipements remis à leur place. En cas de nettoyage excessif nécessaire après le départ, des frais de ménage de 50 à 200 TND seront déduits de la caution.`
    },
    {
      title: "Article 6 — Interdiction de vol et responsabilité",
      text: `Tout vol, tentative de vol, ou détournement d'objets, meubles, équipements électroménagers, linge, décoration ou tout autre bien présent dans le logement est strictement interdit et fera l'objet de poursuites judiciaires. Un inventaire détaillé est remis au preneur à l'entrée. Tout objet manquant lors de l'état des lieux de sortie sera facturé à sa valeur de remplacement, majorée de 30% de frais administratifs.`
    },
    {
      title: "Article 7 — Dégradations et casse",
      text: `Le preneur est responsable de toute dégradation, casse ou détérioration constatée pendant le séjour, y compris celles causées par ses invités. Cela inclut sans s'y limiter : murs, sols, plomberie, serrures, vitres, meubles, électroménager, literie et équipements sanitaires. Les réparations seront effectuées aux frais du preneur. Le montant sera déduit de la caution ou facturé séparément si la caution est insuffisante.`
    },
    {
      title: "Article 8 — Nuisances sonores",
      text: `Le preneur s'engage à respecter la tranquillité du voisinage en toutes circonstances. Le volume sonore (musique, télévision, conversations) doit rester à un niveau raisonnable. Entre 22h00 et 08h00, tout bruit excessif est strictement interdit conformément à la réglementation en vigueur. En cas de plainte du voisinage, le bailleur se réserve le droit de mettre fin immédiatement au séjour sans remboursement.`
    },
  ];

  // Clause spéciale fêtes
  if (dur >= 1) {
    clauses.push({
      title: "Article 9 — Événements et fêtes",
      text: `L'organisation de fêtes, soirées ou rassemblements est soumise à l'accord préalable écrit du bailleur. Si autorisée, toute fête ou événement doit impérativement se terminer avant 00h00 (minuit). Le nombre d'invités ne doit pas dépasser la capacité maximale du logement. Le preneur est responsable du comportement de ses invités et de tout dommage qu'ils pourraient causer. En cas de non-respect de ces règles, le bailleur se réserve le droit de résilier le contrat immédiatement et de retenir la totalité de la caution.`
    });
  }

  clauses.push({
    title: `Article ${clauses.length + 1} — Capacité d'accueil`,
    text: `Le nombre d'occupants ne doit pas dépasser ${property?.bedrooms ? parseInt(property.bedrooms) * 2 + 1 : 4} personnes. Tout occupant supplémentaire non déclaré constitue une violation du présent contrat. Le bailleur se réserve le droit de vérifier le nombre d'occupants à tout moment.`
  });

  clauses.push({
    title: `Article ${clauses.length + 1} — Interdictions`,
    text: `Il est strictement interdit de : fumer à l'intérieur du logement (amende de 200 TND) ; introduire des animaux sans accord écrit préalable ; effectuer des modifications, percements ou aménagements ; sous-louer ou céder le logement à un tiers ; utiliser le logement à des fins commerciales ou illégales ; stocker des matières dangereuses ou inflammables.`
  });

  clauses.push({
    title: `Article ${clauses.length + 1} — Clés et accès`,
    text: `Les clés / codes d'accès sont remis au preneur le jour de l'arrivée. Toute perte de clé entraîne des frais de remplacement de la serrure (150 à 400 TND). Le preneur s'engage à ne pas reproduire les clés et à les restituer le jour du départ. Les codes d'accès numériques sont personnels et ne doivent pas être partagés.`
  });

  clauses.push({
    title: `Article ${clauses.length + 1} — Résiliation anticipée`,
    text: `En cas de résiliation par le preneur : annulation plus de 7 jours avant l'arrivée → remboursement intégral moins 10% de frais ; annulation entre 3 et 7 jours → remboursement de 50% ; annulation moins de 3 jours → aucun remboursement. En cas de manquement grave aux clauses du présent contrat, le bailleur peut résilier immédiatement sans remboursement et retenir la totalité de la caution.`
  });

  clauses.push({
    title: `Article ${clauses.length + 1} — Litiges`,
    text: `En cas de litige, les parties s'engagent à rechercher une solution amiable. À défaut, le tribunal compétent du lieu du logement sera seul compétent. Le présent contrat est régi par la législation en vigueur.`
  });

  clauses.push({
    title: `Article ${clauses.length + 1} — Acceptation`,
    text: `En signant le présent contrat, le preneur reconnaît avoir pris connaissance de l'ensemble des clauses ci-dessus, les accepte sans réserve, et s'engage à les respecter intégralement pendant toute la durée du séjour. Le preneur certifie que les informations personnelles fournies sont exactes.`
  });

  return clauses;
};

const ContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [modal, setModal] = useState(false);
  const [viewContract, setViewContract] = useState(null);
  const [signModal, setSignModal] = useState(null);
  const [form, setForm] = useState({});
  const [toast, setToast] = useState(null);
  const [agencyName, setAgencyName] = useState("");

  // Signature state
  const [sigChecks, setSigChecks] = useState({ read: false, rules: false, damage: false, noise: false, clean: false, legal: false });
  const [sigName, setSigName] = useState("");
  const [sigDrawing, setSigDrawing] = useState(false);
  const sigCanvas = useRef(null);
  const sigCtx = useRef(null);
  const sigDrawingRef = useRef(false);

  const load = async () => {
    const [co, p, c, b] = await Promise.all([API.getAll("contracts"), API.getAll("properties"), API.getAll("clients"), API.getAll("bookings")]);
    setContracts(co.filter(x => x.company_id === COMPANY_ID));
    setProperties(p.filter(x => x.company_id === COMPANY_ID));
    setClients(c.filter(x => x.company_id === COMPANY_ID));
    setBookings(b.filter(x => x.company_id === COMPANY_ID));
    const name = await API.getSetting(COMPANY_ID, "agency_name");
    setAgencyName(name || "l'agence");
  };
  useEffect(() => { load(); }, []);

  const getName = (list, id, fields) => { const r = list.find(i => i.id === id); return r ? fields.map(f => r[f]).filter(Boolean).join(" ") : "—"; };
  const getObj = (list, id) => list.find(i => i.id === id);

  const getDuration = (s, e) => { if (!s || !e) return 0; return Math.ceil((new Date(e) - new Date(s)) / 86400000); };

  const openNew = () => {
    setForm({ property_id: "", client_id: "", booking_id: "", type: "short_term", start_date: "", end_date: "", total_amount: "", deposit: "", monthly_rent: "", notes: "" });
    setModal(true);
  };

  // Auto-fill from booking selection
  const onBookingSelect = (bookingId) => {
    const bk = bookings.find(b => b.id === bookingId);
    if (bk) {
      setForm(prev => ({
        ...prev, booking_id: bookingId, property_id: bk.property_id, client_id: bk.client_id,
        start_date: bk.check_in, end_date: bk.check_out, total_amount: bk.amount,
        deposit: Math.round(parseFloat(bk.amount || 0) * 0.3).toString(),
      }));
    }
  };

  const handleSave = async () => {
    if (!form.property_id || !form.client_id || !form.start_date || !form.end_date) {
      setToast({ message: "Remplissez tous les champs obligatoires", type: "error" }); return;
    }
    const dur = getDuration(form.start_date, form.end_date);
    const prop = getObj(properties, form.property_id);
    const client = getObj(clients, form.client_id);
    const clauses = generateClauses({ ...form, duration_nights: dur }, prop, client, agencyName);

    await API.insert("contracts", {
      ...form, company_id: COMPANY_ID, status: "draft", duration_nights: dur,
      check_in_time: "14:00", check_out_time: "12:00", clauses: JSON.stringify(clauses),
      signed_at: null, signature_data: null, signature_name: null,
    });
    setModal(false); setToast({ message: "Contrat créé en brouillon", type: "success" }); load();
  };

  // Signature canvas handlers
  const initCanvas = () => {
    setTimeout(() => {
      const canvas = sigCanvas.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx.strokeStyle = "#E8ECF4";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        sigCtx.current = ctx;
      }
    }, 100);
  };

  const startDraw = (e) => {
    sigDrawingRef.current = true;
    const rect = sigCanvas.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    sigCtx.current.beginPath();
    sigCtx.current.moveTo(x, y);
  };
  const draw = (e) => {
    if (!sigDrawingRef.current) return;
    e.preventDefault();
    const rect = sigCanvas.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    sigCtx.current.lineTo(x, y);
    sigCtx.current.stroke();
    setSigDrawing(true);
  };
  const stopDraw = () => { sigDrawingRef.current = false; };
  const clearSig = () => {
    const canvas = sigCanvas.current;
    if (canvas) { sigCtx.current.clearRect(0, 0, canvas.width, canvas.height); setSigDrawing(false); }
  };

  const allChecked = Object.values(sigChecks).every(Boolean) && sigName.length > 2 && sigDrawing;

  const handleSign = async () => {
    if (!allChecked) return;
    const sigData = sigCanvas.current?.toDataURL() || "";
    await API.update("contracts", signModal.id, {
      status: "active", signed_at: new Date().toISOString(),
      signature_data: sigData, signature_name: sigName, signature_method: "electronic",
    });
    setSignModal(null); setSigChecks({ read: false, rules: false, damage: false, noise: false, clean: false, legal: false });
    setSigName(""); setSigDrawing(false);
    setToast({ message: "Contrat signé avec succès !", type: "success" }); load();
  };

  const typeLabels = { short_term: "Court terme", lease: "Location", sublease: "Sous-location", commercial: "Commercial", event: "Événement" };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 12 }}>
          <KPI icon={FileText} label="Total" value={contracts.length.toString()} color={T.primary} glow={T.primaryGlow} />
          <KPI icon={Check} label="Signés" value={contracts.filter(c => c.status === "active").length.toString()} color={T.accent} glow={T.accentGlow} />
          <KPI icon={Clock} label="Brouillons" value={contracts.filter(c => c.status === "draft").length.toString()} color={T.warning} glow="rgba(245,158,11,0.15)" />
        </div>
        <Btn onClick={openNew} icon={Plus}>Nouveau Contrat</Btn>
      </div>

      {contracts.length === 0 ? (
        <EmptyState icon={FileText} title="Aucun contrat" subtitle="Créez un contrat depuis une réservation ou manuellement" action="Créer" onAction={openNew} />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
          {contracts.map(c => {
            const dur = c.duration_nights || getDuration(c.start_date, c.end_date);
            return (
              <div key={c.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{getName(properties, c.property_id, ["name"])}</div>
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{getName(clients, c.client_id, ["first_name", "last_name"])}</div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                  <div><div style={{ fontSize: 11, color: T.textMuted }}>Type</div><div style={{ fontSize: 13, color: T.text }}>{typeLabels[c.type] || c.type}</div></div>
                  <div><div style={{ fontSize: 11, color: T.textMuted }}>Durée</div><div style={{ fontSize: 13, color: T.text }}>{dur} nuit(s)</div></div>
                  <div><div style={{ fontSize: 11, color: T.textMuted }}>Entrée 14h</div><div style={{ fontSize: 13, color: T.accent }}>{c.start_date}</div></div>
                  <div><div style={{ fontSize: 11, color: T.textMuted }}>Sortie 12h</div><div style={{ fontSize: 13, color: T.warning }}>{c.end_date}</div></div>
                  <div><div style={{ fontSize: 11, color: T.textMuted }}>Montant</div><div style={{ fontSize: 13, color: T.primary, fontWeight: 600 }}>{parseFloat(c.total_amount || c.monthly_rent || 0).toLocaleString()} TND</div></div>
                  <div><div style={{ fontSize: 11, color: T.textMuted }}>Caution</div><div style={{ fontSize: 13, color: T.warning, fontWeight: 600 }}>{parseFloat(c.deposit || 0).toLocaleString()} TND</div></div>
                </div>
                {c.signed_at && <div style={{ fontSize: 11, color: T.accent, marginBottom: 10 }}>Signé le {new Date(c.signed_at).toLocaleDateString("fr-FR")} par {c.signature_name}</div>}
                <div style={{ display: "flex", gap: 8, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                  <Btn variant="secondary" onClick={() => setViewContract(c)} style={{ flex: 1, justifyContent: "center", padding: "7px 0" }} icon={Eye}>Voir</Btn>
                  {c.status === "draft" && <Btn variant="success" onClick={() => { setSignModal(c); initCanvas(); }} style={{ flex: 1, justifyContent: "center", padding: "7px 0" }} icon={Pencil}>Signer</Btn>}
                  <Btn variant="danger" onClick={async () => { await API.remove("contracts", c.id); load(); }} style={{ padding: "7px 10px" }} icon={Trash2}></Btn>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE CONTRACT MODAL */}
      <Modal open={modal} onClose={() => setModal(false)} title="Nouveau Contrat" width={640}>
        <div style={{ background: T.primaryGlow, borderRadius: 12, padding: "10px 16px", marginBottom: 16, fontSize: 12, color: T.text }}>
          Le contrat sera généré automatiquement avec toutes les clauses juridiques. Le client devra lire et signer en ligne.
        </div>
        {bookings.filter(b => b.status === "confirmed").length > 0 && (
          <Select label="Depuis une réservation (optionnel)" value={form.booking_id || ""} onChange={onBookingSelect}
            options={[{ value: "", label: "— Créer manuellement —" }, ...bookings.filter(b => b.status === "confirmed").map(b => ({ value: b.id, label: `${getName(clients, b.client_id, ["first_name", "last_name"])} — ${getName(properties, b.property_id, ["name"])} (${b.check_in})` }))]} />
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Select label="Propriété *" value={form.property_id || ""} onChange={v => setForm({ ...form, property_id: v })} options={[{ value: "", label: "Sélectionner..." }, ...properties.map(p => ({ value: p.id, label: p.name }))]} />
          <Select label="Client *" value={form.client_id || ""} onChange={v => setForm({ ...form, client_id: v })} options={[{ value: "", label: "Sélectionner..." }, ...clients.map(c => ({ value: c.id, label: `${c.first_name} ${c.last_name}` }))]} />
          <Select label="Type" value={form.type || "short_term"} onChange={v => setForm({ ...form, type: v })} options={[{ value: "short_term", label: "Court terme (1-30 nuits)" }, { value: "lease", label: "Location longue durée" }, { value: "sublease", label: "Sous-location" }, { value: "event", label: "Événement / Fête" }, { value: "commercial", label: "Commercial" }]} />
          <Input label="Caution (TND)" value={form.deposit || ""} onChange={v => setForm({ ...form, deposit: v })} type="number" placeholder="500" />
          <Input label="Date début (entrée 14h) *" value={form.start_date || ""} onChange={v => setForm({ ...form, start_date: v })} type="date" />
          <Input label="Date fin (sortie 12h) *" value={form.end_date || ""} onChange={v => setForm({ ...form, end_date: v })} type="date" />
        </div>
        {form.start_date && form.end_date && <div style={{ background: T.surfaceAlt, borderRadius: 10, padding: "8px 14px", marginBottom: 14, fontSize: 13, color: T.textMuted }}>Durée : <strong style={{ color: T.text }}>{getDuration(form.start_date, form.end_date)} nuit(s)</strong></div>}
        <Input label="Montant total (TND)" value={form.total_amount || ""} onChange={v => setForm({ ...form, total_amount: v })} type="number" />
        <Input label="Notes internes" value={form.notes || ""} onChange={v => setForm({ ...form, notes: v })} textarea />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
          <Btn variant="secondary" onClick={() => setModal(false)}>Annuler</Btn>
          <Btn onClick={handleSave} icon={Save}>Générer Contrat</Btn>
        </div>
      </Modal>

      {/* VIEW CONTRACT MODAL */}
      <Modal open={!!viewContract} onClose={() => setViewContract(null)} title="Contrat de Location" width={720}>
        {viewContract && (() => {
          const prop = getObj(properties, viewContract.property_id);
          const client = getObj(clients, viewContract.client_id);
          const clauses = viewContract.clauses ? JSON.parse(viewContract.clauses) : generateClauses(viewContract, prop, client, agencyName);
          return (
            <div>
              <div style={{ background: T.surfaceAlt, borderRadius: 12, padding: 16, marginBottom: 20, textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>CONTRAT DE LOCATION</div>
                <div style={{ fontSize: 13, color: T.textMuted }}>{agencyName || "Agence Immobilière"}</div>
                <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>Référence : {viewContract.id?.substring(0, 12).toUpperCase()}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20, padding: 16, background: T.card, borderRadius: 12, border: `1px solid ${T.border}` }}>
                <div><span style={{ fontSize: 11, color: T.textMuted }}>Bien :</span> <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{prop?.name || "—"}</span></div>
                <div><span style={{ fontSize: 11, color: T.textMuted }}>Locataire :</span> <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{client ? `${client.first_name} ${client.last_name}` : "—"}</span></div>
                <div><span style={{ fontSize: 11, color: T.textMuted }}>Entrée :</span> <span style={{ fontSize: 13, color: T.accent }}>{viewContract.start_date} à 14h00</span></div>
                <div><span style={{ fontSize: 11, color: T.textMuted }}>Sortie :</span> <span style={{ fontSize: 13, color: T.warning }}>{viewContract.end_date} à 12h00</span></div>
                <div><span style={{ fontSize: 11, color: T.textMuted }}>Montant :</span> <span style={{ fontSize: 13, color: T.primary, fontWeight: 700 }}>{parseFloat(viewContract.total_amount || viewContract.monthly_rent || 0).toLocaleString()} TND</span></div>
                <div><span style={{ fontSize: 11, color: T.textMuted }}>Caution :</span> <span style={{ fontSize: 13, color: T.warning, fontWeight: 700 }}>{parseFloat(viewContract.deposit || 0).toLocaleString()} TND</span></div>
              </div>
              <div style={{ maxHeight: 400, overflow: "auto", paddingRight: 8 }}>
                {clauses.map((cl, i) => (
                  <div key={i} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>{cl.title}</div>
                    <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6 }}>{cl.text}</div>
                  </div>
                ))}
              </div>
              {viewContract.signed_at && (
                <div style={{ marginTop: 20, padding: 16, background: T.accentGlow, borderRadius: 12, border: `1px solid rgba(16,185,129,0.3)` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <CheckCircle size={18} color={T.accent} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: T.accent }}>Contrat signé électroniquement</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>Par : {viewContract.signature_name} — Le : {new Date(viewContract.signed_at).toLocaleString("fr-FR")}</div>
                  {viewContract.signature_data && <img src={viewContract.signature_data} alt="Signature" style={{ maxWidth: 200, marginTop: 8, borderRadius: 8, border: `1px solid ${T.border}` }} />}
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* SIGNATURE MODAL */}
      <Modal open={!!signModal} onClose={() => { setSignModal(null); setSigChecks({ read: false, rules: false, damage: false, noise: false, clean: false, legal: false }); setSigName(""); setSigDrawing(false); }} title="Signature du Contrat" width={640}>
        {signModal && (
          <div>
            <div style={{ background: "rgba(245,158,11,0.1)", borderRadius: 12, padding: "12px 16px", marginBottom: 20, border: `1px solid rgba(245,158,11,0.3)` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Shield size={16} color={T.warning} />
                <span style={{ fontSize: 13, fontWeight: 600, color: T.warning }}>Engagement juridique</span>
              </div>
              <div style={{ fontSize: 12, color: T.textMuted }}>En signant ce contrat, vous vous engagez légalement à respecter toutes les clauses. Lisez attentivement avant de signer.</div>
            </div>

            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 }}>Je confirme avoir lu et j'accepte :</div>

            {[
              { key: "read", label: "J'ai lu intégralement le contrat et toutes ses clauses" },
              { key: "rules", label: "Je m'engage à respecter le règlement intérieur du logement" },
              { key: "damage", label: "Je suis responsable de toute dégradation, casse ou perte et accepte que les frais soient déduits de ma caution" },
              { key: "noise", label: "Je m'engage à respecter les règles de nuisances sonores (silence après 22h, fin de fête avant 00h)" },
              { key: "clean", label: "Je m'engage à restituer le logement propre et en bon état" },
              { key: "legal", label: "Je certifie que mes informations personnelles sont exactes et j'accepte les conséquences juridiques en cas de manquement" },
            ].map(item => (
              <div key={item.key} onClick={() => setSigChecks(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", marginBottom: 6, borderRadius: 10, cursor: "pointer", background: sigChecks[item.key] ? T.accentGlow : T.surfaceAlt, border: `1px solid ${sigChecks[item.key] ? "rgba(16,185,129,0.3)" : T.border}`, transition: "all 0.2s" }}>
                <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${sigChecks[item.key] ? T.accent : T.border}`, background: sigChecks[item.key] ? T.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  {sigChecks[item.key] && <Check size={12} color="#fff" />}
                </div>
                <span style={{ fontSize: 12, color: sigChecks[item.key] ? T.text : T.textMuted, lineHeight: 1.5 }}>{item.label}</span>
              </div>
            ))}

            <div style={{ marginTop: 20 }}>
              <Input label="Nom complet du signataire *" value={sigName} onChange={setSigName} placeholder="Entrez votre nom complet" />
            </div>

            <div style={{ marginTop: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>Signature manuscrite *</div>
                <button onClick={clearSig} style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 10px", fontSize: 11, color: T.textMuted, cursor: "pointer" }}>Effacer</button>
              </div>
              <canvas ref={sigCanvas} width={540} height={120}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                style={{ width: "100%", height: 120, background: T.surfaceAlt, borderRadius: 12, border: `2px dashed ${sigDrawing ? T.accent : T.border}`, cursor: "crosshair", touchAction: "none" }} />
              {!sigDrawing && <div style={{ fontSize: 11, color: T.textDim, textAlign: "center", marginTop: 4 }}>Dessinez votre signature ci-dessus</div>}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 11, color: allChecked ? T.accent : T.textDim }}>
                {allChecked ? "Tout est validé — Vous pouvez signer" : `${Object.values(sigChecks).filter(Boolean).length}/6 conditions acceptées`}
              </div>
              <Btn onClick={handleSign} variant={allChecked ? "primary" : "secondary"} icon={allChecked ? Check : Lock}
                style={{ opacity: allChecked ? 1 : 0.5, pointerEvents: allChecked ? "auto" : "none" }}>
                Signer le Contrat
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE: FINANCE
   ═══════════════════════════════════════════════════════════ */
const FinancePage = () => {
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [tab, setTab] = useState("payments");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    setPayments((await API.getAll("payments")).filter(p=>p.company_id===COMPANY_ID));
    setExpenses((await API.getAll("expenses")).filter(e=>e.company_id===COMPANY_ID));
  };
  useEffect(() => { load(); }, []);

  const totalRev = payments.reduce((s,p)=>s+(parseFloat(p.amount)||0),0);
  const totalExp = expenses.reduce((s,e)=>s+(parseFloat(e.amount)||0),0);

  return (
    <div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:14, marginBottom:24 }}>
        <KPI icon={DollarSign} label="Revenus" value={`${totalRev.toLocaleString()} TND`} color={T.accent} glow={T.accentGlow}/>
        <KPI icon={Receipt} label="Dépenses" value={`${totalExp.toLocaleString()} TND`} color={T.danger} glow="rgba(239,68,68,0.15)"/>
        <KPI icon={TrendingUp} label="Profit" value={`${(totalRev-totalExp).toLocaleString()} TND`} color={T.purple} glow="rgba(139,92,246,0.15)"/>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:16 }}>
        <Btn variant={tab==="payments"?"primary":"secondary"} onClick={()=>setTab("payments")}>Paiements</Btn>
        <Btn variant={tab==="expenses"?"primary":"secondary"} onClick={()=>setTab("expenses")}>Dépenses</Btn>
        <div style={{ marginLeft:"auto" }}>
          <Btn icon={Plus} onClick={()=>{setForm(tab==="payments"?{amount:"",method:"stripe",reference:""}:{amount:"",category:"maintenance",description:""});setModal(tab)}}>Ajouter</Btn>
        </div>
      </div>

      {tab==="payments" ? (
        payments.length===0 ? <EmptyState icon={CreditCard} title="Aucun paiement" subtitle="Les paiements seront enregistrés ici"/> : (
          <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ borderBottom:`1px solid ${T.border}` }}>
                {["Montant","Méthode","Référence","Statut","Date"].map(h=>(<th key={h} style={{ padding:"12px 16px", textAlign:"left", fontSize:12, fontWeight:600, color:T.textMuted }}>{h}</th>))}
              </tr></thead>
              <tbody>{payments.map(p=>(
                <tr key={p.id} style={{ borderBottom:`1px solid ${T.border}` }}>
                  <td style={{ padding:"12px 16px", fontSize:14, fontWeight:600, color:T.accent }}>{parseFloat(p.amount).toLocaleString()} TND</td>
                  <td style={{ padding:"12px 16px", fontSize:13, color:T.textMuted }}>{p.method}</td>
                  <td style={{ padding:"12px 16px", fontSize:13, color:T.textMuted }}>{p.reference||"—"}</td>
                  <td style={{ padding:"12px 16px" }}><StatusBadge status={p.status||"paid"}/></td>
                  <td style={{ padding:"12px 16px", fontSize:12, color:T.textDim }}>{new Date(p.created_at).toLocaleDateString()}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      ) : (
        expenses.length===0 ? <EmptyState icon={Receipt} title="Aucune dépense" subtitle="Enregistrez vos dépenses ici"/> : (
          <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ borderBottom:`1px solid ${T.border}` }}>
                {["Montant","Catégorie","Description","Date"].map(h=>(<th key={h} style={{ padding:"12px 16px", textAlign:"left", fontSize:12, fontWeight:600, color:T.textMuted }}>{h}</th>))}
              </tr></thead>
              <tbody>{expenses.map(e=>(
                <tr key={e.id} style={{ borderBottom:`1px solid ${T.border}` }}>
                  <td style={{ padding:"12px 16px", fontSize:14, fontWeight:600, color:T.danger }}>{parseFloat(e.amount).toLocaleString()} TND</td>
                  <td style={{ padding:"12px 16px", fontSize:13, color:T.textMuted }}>{e.category}</td>
                  <td style={{ padding:"12px 16px", fontSize:13, color:T.textMuted }}>{e.description||"—"}</td>
                  <td style={{ padding:"12px 16px", fontSize:12, color:T.textDim }}>{new Date(e.created_at).toLocaleDateString()}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      )}

      <Modal open={!!modal} onClose={()=>setModal(null)} title={modal==="payments"?"Nouveau Paiement":"Nouvelle Dépense"}>
        <Input label="Montant (TND)" value={form.amount||""} onChange={v=>setForm({...form,amount:v})} type="number"/>
        {modal==="payments" ? <>
          <Select label="Méthode" value={form.method||"stripe"} onChange={v=>setForm({...form,method:v})} options={[{value:"stripe",label:"Carte (Stripe)"},{value:"paypal",label:"PayPal"},{value:"bank_transfer",label:"Virement"},{value:"cash",label:"Espèces"},{value:"mobile_money",label:"Mobile Money"},{value:"crypto",label:"Crypto"}]}/>
          <Input label="Référence" value={form.reference||""} onChange={v=>setForm({...form,reference:v})}/>
        </> : <>
          <Select label="Catégorie" value={form.category||"maintenance"} onChange={v=>setForm({...form,category:v})} options={[{value:"maintenance",label:"Entretien"},{value:"repair",label:"Réparation"},{value:"tax",label:"Taxes"},{value:"insurance",label:"Assurance"},{value:"charges",label:"Charges"},{value:"decoration",label:"Décoration"},{value:"cleaning",label:"Ménage"},{value:"marketing",label:"Marketing"},{value:"legal",label:"Juridique"},{value:"other",label:"Autre"}]}/>
          <Input label="Description" value={form.description||""} onChange={v=>setForm({...form,description:v})} textarea/>
        </>}
        <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:10 }}>
          <Btn variant="secondary" onClick={()=>setModal(null)}>Annuler</Btn>
          <Btn onClick={async()=>{
            if(modal==="payments") await API.insert("payments",{...form,company_id:COMPANY_ID,status:"paid",currency:"TND"});
            else await API.insert("expenses",{...form,company_id:COMPANY_ID,currency:"TND"});
            setModal(null); load();
          }} icon={Save}>Enregistrer</Btn>
        </div>
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE: MESSAGES — Bot commands, templates, email templates
   ═══════════════════════════════════════════════════════════ */
const MessagesPage = () => {
  const [conversations, setConversations] = useState([]);
  const [tab, setTab] = useState("inbox");
  const [toast, setToast] = useState(null);
  const [waTemplates, setWaTemplates] = useState([]);
  const [tgCommands, setTgCommands] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    (async () => {
      setConversations((await API.getAll("conversations")).filter(c => c.company_id === COMPANY_ID));
      // Load templates or init defaults
      let wa = (await API.getAll("wa_templates")).filter(t => t.company_id === COMPANY_ID);
      if (wa.length === 0) {
        const defaults = [
          { name: "welcome", title: "Message de bienvenue", body: "Bonjour {{client_name}} ! 🏠\n\nBienvenue chez H&S Immo. Comment puis-je vous aider ?\n\n1️⃣ Voir les biens disponibles\n2️⃣ Réserver une visite\n3️⃣ Infos sur un bien\n4️⃣ Parler à un agent", channel: "whatsapp", auto: true },
          { name: "booking_confirm", title: "Confirmation réservation", body: "✅ Réservation confirmée !\n\n🏠 {{property_name}}\n📅 Du {{check_in}} (14h00) au {{check_out}} (12h00)\n💰 Montant : {{amount}} TND\n\nVous recevrez les instructions d'accès 24h avant votre arrivée.\n\nH&S Immo — Votre confiance, notre priorité.", channel: "whatsapp", auto: true },
          { name: "payment_received", title: "Paiement reçu", body: "💳 Paiement reçu !\n\nMontant : {{amount}} TND\nRéférence : {{reference}}\nDate : {{date}}\n\nMerci pour votre confiance.\nH&S Immo", channel: "whatsapp", auto: true },
          { name: "checkin_reminder", title: "Rappel check-in J-1", body: "🔑 Rappel : votre séjour commence demain !\n\n🏠 {{property_name}}\n📅 Arrivée : {{check_in}} à 14h00\n📍 Adresse : {{address}}\n\n🔐 Code d'accès : {{access_code}}\nℹ Instructions : {{instructions}}\n\nBon séjour ! 🌟\nH&S Immo", channel: "whatsapp", auto: true },
          { name: "checkout_reminder", title: "Rappel check-out", body: "🚪 Rappel : check-out aujourd'hui à 12h00\n\n✅ Checklist départ :\n• Clés/badge à remettre\n• Vaisselle lavée et rangée\n• Poubelles vidées\n• Fenêtres fermées\n• Lumières et clim éteintes\n\nMerci et à bientôt !\nH&S Immo", channel: "whatsapp", auto: true },
          { name: "payment_reminder", title: "Rappel paiement", body: "⚠ Rappel de paiement\n\n{{client_name}}, votre paiement de {{amount}} TND est en attente depuis le {{due_date}}.\n\n💳 Lien de paiement : {{payment_link}}\n\nMerci de régulariser dans les plus brefs délais.\nH&S Immo", channel: "whatsapp", auto: true },
          { name: "contract_sign", title: "Contrat à signer", body: "📝 Votre contrat est prêt !\n\n🏠 {{property_name}}\n📅 Du {{start_date}} au {{end_date}}\n\n🔗 Signez en ligne : {{sign_link}}\n\nCe contrat inclut les conditions de séjour, la caution et le règlement intérieur.\n\nH&S Immo", channel: "whatsapp", auto: true },
          { name: "listing_info", title: "Fiche bien", body: "🏠 {{property_name}}\n\n📍 {{address}}\n💰 {{price}} TND / nuit\n🛏 {{bedrooms}} chambres • 🚿 {{bathrooms}} SDB • 📏 {{surface}}m²\n\n{{description}}\n\n👉 Réserver : {{booking_link}}\n📞 Contact : {{phone}}", channel: "whatsapp", auto: false },
        ];
        for (const d of defaults) {
          await API.insert("wa_templates", { ...d, company_id: COMPANY_ID });
        }
        wa = (await API.getAll("wa_templates")).filter(t => t.company_id === COMPANY_ID);
      }
      setWaTemplates(wa);

      let tg = (await API.getAll("tg_commands")).filter(t => t.company_id === COMPANY_ID);
      if (tg.length === 0) {
        const defaults = [
          { command: "/start", description: "Démarrer le bot", response: "🏠 Bienvenue chez *H&S Immo* !\n\nJe suis votre assistant immobilier. Voici ce que je peux faire :\n\n/search — Rechercher un bien\n/book — Réserver\n/status — Vérifier une réservation\n/pay — Payer en ligne\n/contact — Contacter un agent\n/help — Aide" },
          { command: "/search", description: "Rechercher un logement", response: "🔍 *Recherche de bien*\n\nOu souhaitez-vous louer ?\n\n Tunis\n Hammamet\n☀ Sousse\n Sfax\n\nEnvoyez le nom de la ville ou utilisez /search [ville]" },
          { command: "/book", description: "Réserver un bien", response: "📅 *Nouvelle réservation*\n\nEnvoyez les infos suivantes :\n\n1. Nom du bien (ou ID)\n2. Date d'arrivée (JJ/MM/AAAA)\n3. Date de départ (JJ/MM/AAAA)\n\nFormat : /book [bien] [arrivée] [départ]\nExemple : /book VillaCarthage 01/05/2026 07/05/2026" },
          { command: "/status", description: "Vérifier une réservation", response: "📊 *Statut réservation*\n\nEnvoyez votre numéro de réservation :\n/status [REF]\n\nExemple : /status RS-2026-0042" },
          { command: "/pay", description: "Lien de paiement", response: "💳 *Paiement en ligne*\n\nVotre lien de paiement sécurisé :\n{{payment_link}}\n\nMontant : {{amount}} TND\nMéthodes : Carte, PayPal, Virement" },
          { command: "/contact", description: "Contacter un agent", response: "📞 *Contacter H&S Immo*\n\n📱 Téléphone : {{phone}}\n📧 Email : {{email}}\n💬 WhatsApp : {{whatsapp}}\n\n📍 Adresse : {{address}}\n⏰ Horaires : Lun-Sam 9h-18h" },
          { command: "/cancel", description: "Annuler une réservation", response: "❌ *Annulation*\n\nPour annuler, envoyez :\n/cancel [REF]\n\n⚠ Conditions :\n• +7j avant : Remboursement 90%\n• 3-7j avant : 50%\n• -3j : Aucun remboursement" },
          { command: "/help", description: "Aide et commandes", response: "ℹ *Commandes disponibles*\n\n/start — Menu principal\n/search — Rechercher un bien\n/book — Réserver\n/status — Statut réservation\n/pay — Paiement\n/cancel — Annuler\n/contact — Nous contacter\n/help — Cette aide" },
        ];
        for (const d of defaults) {
          await API.insert("tg_commands", { ...d, company_id: COMPANY_ID });
        }
        tg = (await API.getAll("tg_commands")).filter(t => t.company_id === COMPANY_ID);
      }
      setTgCommands(tg);

      let em = (await API.getAll("email_templates")).filter(t => t.company_id === COMPANY_ID);
      if (em.length === 0) {
        const defaults = [
          { name: "booking_confirmation", title: "Confirmation de réservation", subject: "H&S Immo — Confirmation de votre réservation #{{ref}}", body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;background:#0B0F1A;color:#E8ECF4;border-radius:12px;overflow:hidden">
<div style="background:linear-gradient(135deg,#C9A84C,#8B6914);padding:24px;text-align:center">
<div style="font-size:24px;font-weight:800;color:#fff;letter-spacing:1px">H&S IMMO</div>
<div style="font-size:11px;color:rgba(255,255,255,0.7);letter-spacing:2px;margin-top:4px">REAL ESTATE</div>
</div>
<div style="padding:32px 24px">
<div style="font-size:20px;font-weight:700;margin-bottom:16px;color:#C9A84C">✅ Réservation Confirmée</div>
<p>Bonjour {{client_name}},</p>
<p>Votre réservation a été confirmée avec succès.</p>
<div style="background:#161D2F;border:1px solid #1E2A45;border-radius:12px;padding:20px;margin:20px 0">
<div style="display:flex;margin-bottom:12px"><span style="color:#7B8BA6;width:120px">Bien :</span><strong>{{property_name}}</strong></div>
<div style="display:flex;margin-bottom:12px"><span style="color:#7B8BA6;width:120px">Adresse :</span>{{address}}</div>
<div style="display:flex;margin-bottom:12px"><span style="color:#7B8BA6;width:120px">Arrivée :</span><strong style="color:#10B981">{{check_in}} à 14h00</strong></div>
<div style="display:flex;margin-bottom:12px"><span style="color:#7B8BA6;width:120px">Départ :</span><strong style="color:#F59E0B">{{check_out}} à 12h00</strong></div>
<div style="display:flex;margin-bottom:12px"><span style="color:#7B8BA6;width:120px">Durée :</span>{{duration}} nuit(s)</div>
<div style="display:flex"><span style="color:#7B8BA6;width:120px">Montant :</span><strong style="color:#C9A84C;font-size:18px">{{amount}} TND</strong></div>
</div>
<p>Vous recevrez vos instructions d'accès 24h avant votre arrivée.</p>
<a href="{{dashboard_link}}" style="display:inline-block;background:linear-gradient(135deg,#C9A84C,#8B6914);color:#fff;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:600;margin-top:12px">Voir ma réservation</a>
</div>
<div style="background:#111827;padding:16px 24px;text-align:center;border-top:1px solid #1E2A45">
<div style="font-size:12px;color:#4A5568">H&S Immo — {{agency_address}}</div>
<div style="font-size:11px;color:#4A5568;margin-top:4px">{{agency_phone}} • {{agency_email}}</div>
</div></div>` },
          { name: "payment_receipt", title: "Reçu de paiement", subject: "H&S Immo — Reçu de paiement {{amount}} TND", body: "Template reçu paiement — même structure que ci-dessus avec détails paiement" },
          { name: "contract_ready", title: "Contrat prêt à signer", subject: "H&S Immo — Votre contrat est prêt à signer", body: "Template contrat — lien de signature en ligne" },
          { name: "checkin_instructions", title: "Instructions check-in", subject: "H&S Immo — Bienvenue ! Instructions d'accès", body: "Template check-in — code accès, adresse, règles" },
          { name: "payment_reminder", title: "Rappel de paiement", subject: "H&S Immo — Rappel : paiement en attente", body: "Template rappel — montant, date, lien paiement" },
          { name: "review_request", title: "Demande d'avis", subject: "H&S Immo — Comment s'est passé votre séjour ?", body: "Template avis — lien formulaire avis" },
        ];
        for (const d of defaults) {
          await API.insert("email_templates", { ...d, company_id: COMPANY_ID });
        }
        em = (await API.getAll("email_templates")).filter(t => t.company_id === COMPANY_ID);
      }
      setEmailTemplates(em);
    })();
  }, []);

  const saveTemplate = async (table, form) => {
    if (editModal?.id) await API.update(table, editModal.id, form);
    setEditModal(null);
    setToast({ message: "Template sauvegardé", type: "success" });
    // Reload
    if (table === "wa_templates") setWaTemplates((await API.getAll("wa_templates")).filter(t => t.company_id === COMPANY_ID));
    if (table === "tg_commands") setTgCommands((await API.getAll("tg_commands")).filter(t => t.company_id === COMPANY_ID));
    if (table === "email_templates") setEmailTemplates((await API.getAll("email_templates")).filter(t => t.company_id === COMPANY_ID));
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div style={{ display: "flex", gap: 6, marginBottom: 20, background: T.surfaceAlt, borderRadius: 12, padding: 4, border: `1px solid ${T.border}`, width: "fit-content" }}>
        {[["inbox", "Inbox", MessageCircle], ["whatsapp", "WhatsApp Bot", MessageCircle], ["telegram", "Telegram Bot", Send], ["email", "Email Templates", Mail]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 12, fontWeight: tab === id ? 600 : 500, background: tab === id ? T.primary : "transparent", color: tab === id ? "#fff" : T.textMuted }}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {/* INBOX */}
      {tab === "inbox" && (
        conversations.length === 0 ? (
          <EmptyState icon={MessageCircle} title="Aucune conversation" subtitle="Les messages WhatsApp, Telegram et Email apparaîtront ici une fois les bots configurés" />
        ) : (
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20 }}>
            <div style={{ color: T.textMuted, fontSize: 13 }}>Inbox — {conversations.length} conversation(s)</div>
          </div>
        )
      )}

      {/* WHATSAPP TEMPLATES */}
      {tab === "whatsapp" && (
        <div>
          <SectionCard title="Templates WhatsApp Business" description="Messages pré-configurés envoyés automatiquement via l'API WhatsApp Cloud. Variables : {{client_name}}, {{property_name}}, {{check_in}}, {{check_out}}, {{amount}}, etc.">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {waTemplates.map(t => (
                <div key={t.id} style={{ background: T.surfaceAlt, borderRadius: 12, border: `1px solid ${T.border}`, overflow: "hidden" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <MessageCircle size={16} color="#25D366" />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{t.title}</div>
                        <div style={{ fontSize: 11, color: T.textDim }}>{t.name}{t.auto ? " • Auto" : " • Manuel"}</div>
                      </div>
                    </div>
                    <Btn variant="secondary" onClick={() => { setEditModal(t); setEditForm({ title: t.title, body: t.body }); }} style={{ padding: "5px 12px" }} icon={Pencil}>Modifier</Btn>
                  </div>
                  <div style={{ padding: "0 16px 14px", fontSize: 12, color: T.textMuted, whiteSpace: "pre-wrap", lineHeight: 1.5, maxHeight: 80, overflow: "hidden" }}>{t.body}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {/* TELEGRAM COMMANDS */}
      {tab === "telegram" && (
        <div>
          <SectionCard title="Commandes Telegram Bot" description="Commandes disponibles pour les utilisateurs du bot @HSImmoBot">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {tgCommands.map(t => (
                <div key={t.id} style={{ background: T.surfaceAlt, borderRadius: 12, border: `1px solid ${T.border}`, overflow: "hidden" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ background: "rgba(0,136,204,0.15)", borderRadius: 6, padding: "3px 8px" }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0088cc", fontFamily: "monospace" }}>{t.command}</span>
                      </div>
                      <span style={{ fontSize: 12, color: T.textMuted }}>{t.description}</span>
                    </div>
                    <Btn variant="secondary" onClick={() => { setEditModal(t); setEditForm({ description: t.description, response: t.response }); }} style={{ padding: "5px 12px" }} icon={Pencil}>Modifier</Btn>
                  </div>
                  <div style={{ padding: "0 16px 14px", fontSize: 12, color: T.textMuted, whiteSpace: "pre-wrap", lineHeight: 1.5, maxHeight: 80, overflow: "hidden" }}>{t.response}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {/* EMAIL TEMPLATES */}
      {tab === "email" && (
        <div>
          <SectionCard title="Templates Email" description="Emails envoyés automatiquement lors des événements. Design H&S Immo intégré avec en-tête doré.">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {emailTemplates.map(t => (
                <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: T.surfaceAlt, borderRadius: 12, border: `1px solid ${T.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Mail size={16} color={T.primary} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{t.title}</div>
                      <div style={{ fontSize: 11, color: T.textDim }}>{t.subject}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn variant="secondary" onClick={() => { setEditModal(t); setEditForm({ title: t.title, subject: t.subject, body: t.body }); }} style={{ padding: "5px 12px" }} icon={Pencil}>Modifier</Btn>
                    <Btn variant="secondary" onClick={() => setToast({ message: "Aperçu envoyé par email", type: "info" })} style={{ padding: "5px 12px" }} icon={Eye}>Aperçu</Btn>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Email preview */}
          <SectionCard title="Aperçu Template Email" description="Rendu visuel du template de confirmation">
            <div style={{ background: "#0B0F1A", borderRadius: 12, overflow: "hidden", maxWidth: 500, margin: "0 auto", border: `1px solid ${T.border}` }}>
              <div style={{ background: "linear-gradient(135deg,#C9A84C,#8B6914)", padding: "20px", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: 1 }}>H&S IMMO</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", letterSpacing: 2, marginTop: 3 }}>REAL ESTATE</div>
              </div>
              <div style={{ padding: "24px 20px" }}>
                <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 12, color: "#C9A84C" }}>✅ Réservation Confirmée</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>Bonjour <strong style={{ color: T.text }}>Ahmed Ben Ali</strong>,<br />Votre réservation a été confirmée.</div>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, fontSize: 12, color: T.textMuted, lineHeight: 2 }}>
                  <div>Bien : <strong style={{ color: T.text }}>Villa Carthage</strong></div>
                  <div>Arrivée : <strong style={{ color: T.accent }}>01/04/2026 à 14h00</strong></div>
                  <div>Départ : <strong style={{ color: T.warning }}>07/04/2026 à 12h00</strong></div>
                  <div>Durée : <strong style={{ color: T.text }}>6 nuits</strong></div>
                  <div>Montant : <strong style={{ color: "#C9A84C", fontSize: 15 }}>3,500 TND</strong></div>
                </div>
                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <div style={{ display: "inline-block", background: "linear-gradient(135deg,#C9A84C,#8B6914)", color: "#fff", padding: "10px 24px", borderRadius: 8, fontSize: 13, fontWeight: 600 }}>Voir ma réservation</div>
                </div>
              </div>
              <div style={{ background: T.surface, padding: "12px 20px", textAlign: "center", borderTop: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 10, color: T.textDim }}>H&S Immo — Tunis, Tunisie</div>
                <div style={{ fontSize: 10, color: T.textDim }}>contact@hsimmo.tn • +216 71 234 567</div>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* EDIT MODAL */}
      <Modal open={!!editModal} onClose={() => setEditModal(null)} title="Modifier le template" width={600}>
        {editModal && (
          <div>
            {editForm.title !== undefined && <Input label="Titre" value={editForm.title || ""} onChange={v => setEditForm({ ...editForm, title: v })} />}
            {editForm.subject !== undefined && <Input label="Sujet email" value={editForm.subject || ""} onChange={v => setEditForm({ ...editForm, subject: v })} />}
            {editForm.description !== undefined && <Input label="Description" value={editForm.description || ""} onChange={v => setEditForm({ ...editForm, description: v })} />}
            {editForm.body !== undefined && <Input label="Corps du message" value={editForm.body || ""} onChange={v => setEditForm({ ...editForm, body: v })} textarea />}
            {editForm.response !== undefined && <Input label="Réponse du bot" value={editForm.response || ""} onChange={v => setEditForm({ ...editForm, response: v })} textarea />}
            <div style={{ fontSize: 11, color: T.textDim, marginTop: 8, padding: "8px 12px", background: T.surfaceAlt, borderRadius: 8 }}>
              Variables disponibles : {"{{client_name}}, {{property_name}}, {{check_in}}, {{check_out}}, {{amount}}, {{address}}, {{phone}}, {{email}}, {{payment_link}}, {{sign_link}}, {{access_code}}, {{ref}}"}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
              <Btn variant="secondary" onClick={() => setEditModal(null)}>Annuler</Btn>
              <Btn onClick={() => {
                const table = editModal.command ? "tg_commands" : editModal.channel ? "wa_templates" : "email_templates";
                saveTemplate(table, editForm);
              }} icon={Save}>Sauvegarder</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE: SOCIAL
   ═══════════════════════════════════════════════════════════ */
const SocialPage = () => {
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { (async()=>setAccounts((await API.getAll("social_accounts")).filter(a=>a.company_id===COMPANY_ID)))(); }, []);
  return (
    <div>
      {accounts.length === 0 ? (
        <EmptyState icon={Share2} title="Aucun réseau social connecté" subtitle="Connectez vos comptes Facebook, Instagram et TikTok dans Paramètres → Réseaux Sociaux"/>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:16 }}>
          {accounts.map(a=>(
            <div key={a.id} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:20 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                <ChannelIcon channel={a.platform} size={20}/>
                <span style={{ fontSize:14, fontWeight:600, color:T.text }}>{a.platform}</span>
                <StatusBadge status={a.status}/>
              </div>
              <div style={{ fontSize:13, color:T.textMuted }}>{a.account_name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE: SETTINGS (SUPER ADMIN)
   Full configuration for all integrations
   ═══════════════════════════════════════════════════════════ */
const SettingsPage = () => {
  const [tab, setTab] = useState("agency");
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      const all = await API.getSettings(COMPANY_ID);
      const map = {};
      all.forEach(s => { map[s.key] = s.value; });
      setSettings(map);
      setLoading(false);
    })();
  }, []);

  const set = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const saveCategory = async (category, keys) => {
    for (const k of keys) {
      await API.setSetting(COMPANY_ID, k, settings[k] || "", category);
    }
    setToast({ message: "Paramètres sauvegardés", type: "success" });
  };

  const tabs = [
    { id:"agency", label:"Agence", icon:Building2 },
    { id:"api", label:"API & Webhooks", icon:Webhook },
    { id:"whatsapp", label:"WhatsApp", icon:MessageCircle },
    { id:"telegram", label:"Telegram", icon:Send },
    { id:"email", label:"Email / SMTP", icon:Mail },
    { id:"social", label:"Réseaux Sociaux", icon:Share2 },
    { id:"payments", label:"Paiements", icon:CreditCard },
    { id:"channels", label:"Channel Manager", icon:Globe },
    { id:"security", label:"Sécurité", icon:Shield },
    { id:"database", label:"Base de Données", icon:Database },
  ];

  return (
    <div style={{ display:"grid", gridTemplateColumns:"220px 1fr", gap:20 }}>
      {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}

      {/* Settings Nav */}
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:8, height:"fit-content", position:"sticky", top:0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            display:"flex", alignItems:"center", gap:8, width:"100%", padding:"10px 12px", borderRadius:10, border:"none", cursor:"pointer", marginBottom:2,
            background:tab===t.id?T.primaryGlow:"transparent", color:tab===t.id?T.primary:T.textMuted, fontSize:13, fontWeight:tab===t.id?600:500,
          }}>
            <t.icon size={16}/>{t.label}
          </button>
        ))}
      </div>

      {/* Settings Content */}
      <div>
        {/* AGENCY */}
        {tab==="agency" && (
          <SectionCard title="Informations Agence" description="Configurez les informations de votre agence immobilière"
            actions={<Btn icon={Save} onClick={()=>saveCategory("agency",["agency_name","agency_email","agency_phone","agency_address","agency_city","agency_country","agency_currency","agency_timezone","agency_logo_url"])}>Sauvegarder</Btn>}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
              <Input label="Nom de l'agence" value={settings.agency_name||""} onChange={v=>set("agency_name",v)} placeholder="ImmoTech Tunisia" icon={Building2}/>
              <Input label="Email" value={settings.agency_email||""} onChange={v=>set("agency_email",v)} placeholder="contact@immotech.tn" icon={Mail}/>
              <Input label="Téléphone" value={settings.agency_phone||""} onChange={v=>set("agency_phone",v)} placeholder="+216 71 234 567" icon={Phone}/>
              <Input label="Adresse" value={settings.agency_address||""} onChange={v=>set("agency_address",v)} placeholder="Les Berges du Lac" icon={MapPin}/>
              <Input label="Ville" value={settings.agency_city||""} onChange={v=>set("agency_city",v)} placeholder="Tunis"/>
              <Input label="Pays" value={settings.agency_country||""} onChange={v=>set("agency_country",v)} placeholder="Tunisie"/>
              <Select label="Devise" value={settings.agency_currency||"TND"} onChange={v=>set("agency_currency",v)} options={[{value:"TND",label:"TND - Dinar Tunisien"},{value:"EUR",label:"EUR - Euro"},{value:"USD",label:"USD - Dollar"},{value:"MAD",label:"MAD - Dirham Marocain"},{value:"DZD",label:"DZD - Dinar Algérien"}]}/>
              <Select label="Fuseau horaire" value={settings.agency_timezone||"Africa/Tunis"} onChange={v=>set("agency_timezone",v)} options={[{value:"Africa/Tunis",label:"Africa/Tunis (GMT+1)"},{value:"Europe/Paris",label:"Europe/Paris (GMT+1)"},{value:"Europe/London",label:"Europe/London (GMT+0)"}]}/>
            </div>
            <Input label="URL Logo" value={settings.agency_logo_url||""} onChange={v=>set("agency_logo_url",v)} placeholder="https://..." icon={Link}/>
          </SectionCard>
        )}

        {/* API & WEBHOOKS */}
        {tab==="api" && (<>
          <SectionCard title="Clés API" description="Vos clés d'accès à l'API REST de la plateforme"
            actions={<Btn icon={Save} onClick={()=>saveCategory("api",["api_key","api_secret","webhook_url","webhook_secret","api_rate_limit"])}>Sauvegarder</Btn>}>
            <Input label="API Key" value={settings.api_key||""} onChange={v=>set("api_key",v)} placeholder="pk_live_..." icon={Key}/>
            <Input label="API Secret" value={settings.api_secret||""} onChange={v=>set("api_secret",v)} placeholder="sk_live_..." type="password" icon={Lock}/>
            <Input label="Rate Limit (req/min)" value={settings.api_rate_limit||"60"} onChange={v=>set("api_rate_limit",v)} type="number"/>
          </SectionCard>
          <SectionCard title="Webhooks" description="URLs de callback pour recevoir les événements en temps réel">
            <Input label="Webhook URL" value={settings.webhook_url||""} onChange={v=>set("webhook_url",v)} placeholder="https://votre-serveur.com/webhook" icon={Webhook}/>
            <Input label="Webhook Secret" value={settings.webhook_secret||""} onChange={v=>set("webhook_secret",v)} placeholder="whsec_..." type="password" icon={Lock}/>
            <div style={{ fontSize:12, color:T.textMuted, marginTop:8 }}>Événements envoyés : booking.created, booking.confirmed, payment.received, contract.signed, client.created, maintenance.created</div>
          </SectionCard>
        </>)}

        {/* WHATSAPP */}
        {tab==="whatsapp" && (<>
          <SectionCard title="WhatsApp Business API" description="Configuration du bot WhatsApp pour la communication client"
            actions={<Btn icon={Save} onClick={()=>saveCategory("whatsapp",["wa_phone_number_id","wa_business_account_id","wa_access_token","wa_verify_token","wa_webhook_url","wa_bot_enabled","wa_auto_reply","wa_welcome_message","wa_catalog_enabled"])}>Sauvegarder</Btn>}>
            <div style={{ background:T.primaryGlow, borderRadius:12, padding:"12px 16px", marginBottom:16, display:"flex", alignItems:"center", gap:10 }}>
              <MessageCircle size={18} color="#25D366"/>
              <div style={{ fontSize:13, color:T.text }}>Utilisez la <strong>WhatsApp Cloud API</strong> (Meta Business). Obtenez vos credentials sur <span style={{ color:T.primary }}>developers.facebook.com</span></div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
              <Input label="Phone Number ID" value={settings.wa_phone_number_id||""} onChange={v=>set("wa_phone_number_id",v)} placeholder="1234567890"/>
              <Input label="Business Account ID" value={settings.wa_business_account_id||""} onChange={v=>set("wa_business_account_id",v)} placeholder="9876543210"/>
            </div>
            <Input label="Access Token (permanent)" value={settings.wa_access_token||""} onChange={v=>set("wa_access_token",v)} placeholder="EAAxxxxxxx..." type="password" icon={Key}/>
            <Input label="Verify Token (webhook)" value={settings.wa_verify_token||""} onChange={v=>set("wa_verify_token",v)} placeholder="my_verify_token_123" icon={Shield}/>
            <Input label="Webhook URL (auto-généré)" value={settings.wa_webhook_url||`https://api.votredomaine.com/webhooks/whatsapp`} onChange={v=>set("wa_webhook_url",v)} icon={Webhook}/>
            <Toggle label="Bot WhatsApp activé" value={settings.wa_bot_enabled==="true"} onChange={v=>set("wa_bot_enabled",v.toString())} description="Réponses automatiques aux messages entrants"/>
            <Toggle label="Auto-reply" value={settings.wa_auto_reply==="true"} onChange={v=>set("wa_auto_reply",v.toString())} description="Répondre automatiquement hors horaires"/>
            <Toggle label="Catalogue biens" value={settings.wa_catalog_enabled==="true"} onChange={v=>set("wa_catalog_enabled",v.toString())} description="Envoyer la liste des biens via WhatsApp Interactive"/>
            <Input label="Message de bienvenue" value={settings.wa_welcome_message||""} onChange={v=>set("wa_welcome_message",v)} textarea placeholder="Bonjour ! Bienvenue chez {agency_name}. Comment puis-je vous aider ? &#10;&#10;1️⃣ Voir les biens disponibles&#10;2️⃣ Réserver une visite&#10;3️⃣ Contacter un agent"/>
          </SectionCard>
        </>)}

        {/* TELEGRAM */}
        {tab==="telegram" && (<>
          <SectionCard title="Telegram Bot" description="Configuration du bot Telegram"
            actions={<Btn icon={Save} onClick={()=>saveCategory("telegram",["tg_bot_token","tg_bot_username","tg_webhook_url","tg_admin_chat_id","tg_bot_enabled","tg_welcome_message","tg_inline_search"])}>Sauvegarder</Btn>}>
            <div style={{ background:"rgba(0,136,204,0.1)", borderRadius:12, padding:"12px 16px", marginBottom:16, display:"flex", alignItems:"center", gap:10 }}>
              <Send size={18} color="#0088cc"/>
              <div style={{ fontSize:13, color:T.text }}>Créez votre bot via <strong>@BotFather</strong> sur Telegram pour obtenir le token</div>
            </div>
            <Input label="Bot Token" value={settings.tg_bot_token||""} onChange={v=>set("tg_bot_token",v)} placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11" type="password" icon={Key}/>
            <Input label="Bot Username" value={settings.tg_bot_username||""} onChange={v=>set("tg_bot_username",v)} placeholder="@ImmoTechBot"/>
            <Input label="Webhook URL" value={settings.tg_webhook_url||`https://api.votredomaine.com/webhooks/telegram`} onChange={v=>set("tg_webhook_url",v)} icon={Webhook}/>
            <Input label="Admin Chat ID (notifications)" value={settings.tg_admin_chat_id||""} onChange={v=>set("tg_admin_chat_id",v)} placeholder="-1001234567890"/>
            <Toggle label="Bot activé" value={settings.tg_bot_enabled==="true"} onChange={v=>set("tg_bot_enabled",v.toString())} description="Commandes : /search, /book, /status, /contact"/>
            <Toggle label="Recherche inline" value={settings.tg_inline_search==="true"} onChange={v=>set("tg_inline_search",v.toString())} description="Permettre la recherche de biens en mode inline"/>
            <Input label="Message de bienvenue" value={settings.tg_welcome_message||""} onChange={v=>set("tg_welcome_message",v)} textarea placeholder="👋 Bienvenue sur ImmoTech Bot !&#10;&#10;/search - Rechercher un bien&#10;/book - Réserver&#10;/status - Vérifier une réservation&#10;/contact - Contacter un agent"/>
          </SectionCard>
        </>)}

        {/* EMAIL / SMTP */}
        {tab==="email" && (<>
          <SectionCard title="Configuration SMTP" description="Serveur email pour notifications et communications"
            actions={<Btn icon={Save} onClick={()=>saveCategory("email",["smtp_host","smtp_port","smtp_user","smtp_password","smtp_encryption","smtp_from_name","smtp_from_email","email_provider"])}>Sauvegarder</Btn>}>
            <Select label="Provider" value={settings.email_provider||"smtp"} onChange={v=>set("email_provider",v)} options={[{value:"smtp",label:"SMTP Custom"},{value:"resend",label:"Resend"},{value:"postmark",label:"Postmark"},{value:"sendgrid",label:"SendGrid"},{value:"ses",label:"Amazon SES"}]}/>
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:"0 16px" }}>
              <Input label="SMTP Host" value={settings.smtp_host||""} onChange={v=>set("smtp_host",v)} placeholder="smtp.gmail.com" icon={Server}/>
              <Input label="Port" value={settings.smtp_port||""} onChange={v=>set("smtp_port",v)} placeholder="587"/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
              <Input label="Utilisateur" value={settings.smtp_user||""} onChange={v=>set("smtp_user",v)} placeholder="email@domaine.com"/>
              <Input label="Mot de passe" value={settings.smtp_password||""} onChange={v=>set("smtp_password",v)} type="password"/>
            </div>
            <Select label="Chiffrement" value={settings.smtp_encryption||"tls"} onChange={v=>set("smtp_encryption",v)} options={[{value:"tls",label:"TLS"},{value:"ssl",label:"SSL"},{value:"none",label:"Aucun"}]}/>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
              <Input label="Nom expéditeur" value={settings.smtp_from_name||""} onChange={v=>set("smtp_from_name",v)} placeholder="ImmoTech"/>
              <Input label="Email expéditeur" value={settings.smtp_from_email||""} onChange={v=>set("smtp_from_email",v)} placeholder="noreply@immotech.tn"/>
            </div>
          </SectionCard>
          <SectionCard title="Templates Email" description="Configurez les templates de notification">
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {["Confirmation réservation","Rappel paiement","Contrat à signer","Bienvenue client","Contrat expiré","Relance impayé"].map(t => (
                <div key={t} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:T.surfaceAlt, borderRadius:10, border:`1px solid ${T.border}` }}>
                  <span style={{ fontSize:13, color:T.text }}>{t}</span>
                  <Btn variant="secondary" style={{ padding:"5px 12px" }}>Modifier</Btn>
                </div>
              ))}
            </div>
          </SectionCard>
        </>)}

        {/* SOCIAL MEDIA */}
        {tab==="social" && (<>
          <SectionCard title="Facebook" description="Connectez votre page Facebook"
            actions={<Btn icon={Save} onClick={()=>saveCategory("social",["fb_page_id","fb_access_token","fb_app_id","fb_app_secret","fb_pixel_id","ig_account_id","ig_access_token","tiktok_business_id","tiktok_access_token"])}>Sauvegarder</Btn>}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
              <Input label="App ID" value={settings.fb_app_id||""} onChange={v=>set("fb_app_id",v)} placeholder="123456789" icon={Facebook}/>
              <Input label="App Secret" value={settings.fb_app_secret||""} onChange={v=>set("fb_app_secret",v)} type="password" icon={Lock}/>
              <Input label="Page ID" value={settings.fb_page_id||""} onChange={v=>set("fb_page_id",v)} placeholder="987654321"/>
              <Input label="Page Access Token" value={settings.fb_access_token||""} onChange={v=>set("fb_access_token",v)} type="password" icon={Key}/>
            </div>
            <Input label="Pixel ID (tracking)" value={settings.fb_pixel_id||""} onChange={v=>set("fb_pixel_id",v)} placeholder="1234567890"/>
          </SectionCard>

          <SectionCard title="Instagram" description="Connectez votre compte Instagram Business">
            <Input label="Account ID" value={settings.ig_account_id||""} onChange={v=>set("ig_account_id",v)} placeholder="17841234567890" icon={Instagram}/>
            <Input label="Access Token" value={settings.ig_access_token||""} onChange={v=>set("ig_access_token",v)} type="password" icon={Key}/>
            <div style={{ fontSize:12, color:T.textMuted }}>L'accès Instagram utilise la même App Facebook. Activez l'API Instagram Graph dans votre dashboard Meta.</div>
          </SectionCard>

          <SectionCard title="TikTok" description="Connectez votre compte TikTok Business">
            <Input label="Business ID" value={settings.tiktok_business_id||""} onChange={v=>set("tiktok_business_id",v)} placeholder="7123456789" icon={Hash}/>
            <Input label="Access Token" value={settings.tiktok_access_token||""} onChange={v=>set("tiktok_access_token",v)} type="password" icon={Key}/>
            <div style={{ fontSize:12, color:T.textMuted }}>Obtenez vos credentials sur developers.tiktok.com → Marketing API.</div>
          </SectionCard>
        </>)}

        {/* PAYMENTS */}
        {tab==="payments" && (<>
          <SectionCard title="Stripe" description="Paiements par carte bancaire"
            actions={<Btn icon={Save} onClick={()=>saveCategory("payments",["stripe_pk","stripe_sk","stripe_webhook_secret","paypal_client_id","paypal_secret","paypal_mode","payment_currency","payment_auto_invoice"])}>Sauvegarder</Btn>}>
            <Input label="Publishable Key" value={settings.stripe_pk||""} onChange={v=>set("stripe_pk",v)} placeholder="pk_live_..." icon={Key}/>
            <Input label="Secret Key" value={settings.stripe_sk||""} onChange={v=>set("stripe_sk",v)} type="password" placeholder="sk_live_..." icon={Lock}/>
            <Input label="Webhook Secret" value={settings.stripe_webhook_secret||""} onChange={v=>set("stripe_webhook_secret",v)} type="password" placeholder="whsec_..." icon={Shield}/>
          </SectionCard>
          <SectionCard title="PayPal" description="Paiements PayPal">
            <Input label="Client ID" value={settings.paypal_client_id||""} onChange={v=>set("paypal_client_id",v)} placeholder="AX..." icon={Key}/>
            <Input label="Secret" value={settings.paypal_secret||""} onChange={v=>set("paypal_secret",v)} type="password" icon={Lock}/>
            <Select label="Mode" value={settings.paypal_mode||"sandbox"} onChange={v=>set("paypal_mode",v)} options={[{value:"sandbox",label:"Sandbox (test)"},{value:"live",label:"Production"}]}/>
          </SectionCard>
          <SectionCard title="Options Paiement">
            <Select label="Devise par défaut" value={settings.payment_currency||"TND"} onChange={v=>set("payment_currency",v)} options={[{value:"TND",label:"TND"},{value:"EUR",label:"EUR"},{value:"USD",label:"USD"}]}/>
            <Toggle label="Factures automatiques" value={settings.payment_auto_invoice==="true"} onChange={v=>set("payment_auto_invoice",v.toString())} description="Générer et envoyer une facture PDF à chaque paiement"/>
          </SectionCard>
        </>)}

        {/* CHANNEL MANAGER */}
        {tab==="channels" && (
          <SectionCard title="Channel Manager (OTA)" description="Synchronisation avec les plateformes de réservation"
            actions={<Btn icon={Save} onClick={()=>saveCategory("channels",["airbnb_ical_url","airbnb_api_key","booking_hotel_id","booking_api_key","expedia_property_id","expedia_api_key"])}>Sauvegarder</Btn>}>
            <div style={{ background:T.primaryGlow, borderRadius:12, padding:"12px 16px", marginBottom:16, fontSize:13, color:T.text }}>
              Synchronisez vos calendriers et tarifs avec Airbnb, Booking.com et Expedia pour éviter les double-réservations.
            </div>
            <div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:12 }}>Airbnb</div>
            <Input label="iCal URL (import)" value={settings.airbnb_ical_url||""} onChange={v=>set("airbnb_ical_url",v)} placeholder="https://www.airbnb.com/calendar/ical/..." icon={Link}/>
            <Input label="API Key (optionnel)" value={settings.airbnb_api_key||""} onChange={v=>set("airbnb_api_key",v)} type="password" icon={Key}/>
            <div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:12, marginTop:16 }}>Booking.com</div>
            <Input label="Hotel ID" value={settings.booking_hotel_id||""} onChange={v=>set("booking_hotel_id",v)} placeholder="1234567"/>
            <Input label="API Key" value={settings.booking_api_key||""} onChange={v=>set("booking_api_key",v)} type="password" icon={Key}/>
            <div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:12, marginTop:16 }}>Expedia / Vrbo</div>
            <Input label="Property ID" value={settings.expedia_property_id||""} onChange={v=>set("expedia_property_id",v)}/>
            <Input label="API Key" value={settings.expedia_api_key||""} onChange={v=>set("expedia_api_key",v)} type="password" icon={Key}/>
          </SectionCard>
        )}

        {/* SECURITY */}
        {tab==="security" && (
          <SectionCard title="Sécurité" description="Configuration de sécurité et authentification"
            actions={<Btn icon={Save} onClick={()=>saveCategory("security",["jwt_secret","jwt_expiry","enable_2fa","enable_oauth","oauth_google_id","oauth_google_secret","cors_origins","rate_limit_api"])}>Sauvegarder</Btn>}>
            <Input label="JWT Secret" value={settings.jwt_secret||""} onChange={v=>set("jwt_secret",v)} type="password" placeholder="super_secret_key_256bit" icon={Lock}/>
            <Input label="JWT Expiry (heures)" value={settings.jwt_expiry||"24"} onChange={v=>set("jwt_expiry",v)} type="number"/>
            <Toggle label="Authentification 2FA" value={settings.enable_2fa==="true"} onChange={v=>set("enable_2fa",v.toString())} description="TOTP via Google Authenticator"/>
            <Toggle label="OAuth2 (Google Login)" value={settings.enable_oauth==="true"} onChange={v=>set("enable_oauth",v.toString())}/>
            {settings.enable_oauth==="true" && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px", marginTop:10 }}>
                <Input label="Google Client ID" value={settings.oauth_google_id||""} onChange={v=>set("oauth_google_id",v)} icon={Key}/>
                <Input label="Google Client Secret" value={settings.oauth_google_secret||""} onChange={v=>set("oauth_google_secret",v)} type="password" icon={Lock}/>
              </div>
            )}
            <Input label="CORS Origins" value={settings.cors_origins||""} onChange={v=>set("cors_origins",v)} placeholder="https://monsite.com, https://app.monsite.com"/>
            <Input label="Rate Limit API (req/min)" value={settings.rate_limit_api||"100"} onChange={v=>set("rate_limit_api",v)} type="number"/>
          </SectionCard>
        )}

        {/* DATABASE */}
        {tab==="database" && (
          <SectionCard title="Base de Données" description="Schéma et connexion PostgreSQL"
            actions={<Btn icon={Save} onClick={()=>saveCategory("database",["db_host","db_port","db_name","db_user","db_password","db_ssl","redis_url"])}>Sauvegarder</Btn>}>
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:"0 16px" }}>
              <Input label="Host" value={settings.db_host||""} onChange={v=>set("db_host",v)} placeholder="db.supabase.co" icon={Server}/>
              <Input label="Port" value={settings.db_port||"5432"} onChange={v=>set("db_port",v)} placeholder="5432"/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
              <Input label="Base de données" value={settings.db_name||""} onChange={v=>set("db_name",v)} placeholder="immotech_prod" icon={Database}/>
              <Input label="Utilisateur" value={settings.db_user||""} onChange={v=>set("db_user",v)} placeholder="postgres"/>
            </div>
            <Input label="Mot de passe" value={settings.db_password||""} onChange={v=>set("db_password",v)} type="password" icon={Lock}/>
            <Toggle label="SSL" value={settings.db_ssl==="true"} onChange={v=>set("db_ssl",v.toString())} description="Connexion chiffrée à la base"/>
            <Input label="Redis URL" value={settings.redis_url||""} onChange={v=>set("redis_url",v)} placeholder="redis://default:xxx@redis-host:6379" icon={Server}/>

            <div style={{ marginTop:20, padding:16, background:T.surfaceAlt, borderRadius:12, border:`1px solid ${T.border}` }}>
              <div style={{ fontSize:14, fontWeight:600, color:T.text, marginBottom:12 }}>Schéma DB — {Object.keys(DB_SCHEMA).length} tables</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:8 }}>
                {Object.entries(DB_SCHEMA).map(([table, schema]) => (
                  <div key={table} style={{ padding:"8px 12px", background:T.card, borderRadius:8, border:`1px solid ${T.border}` }}>
                    <div style={{ fontSize:12, fontWeight:600, color:T.primary }}>{table}</div>
                    <div style={{ fontSize:10, color:T.textDim }}>{schema.fields.length} colonnes</div>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE: USERS MANAGEMENT (Super Admin only)
   ═══════════════════════════════════════════════════════════ */
const UsersPage = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState("users");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = async () => {
    setUsers(await AUTH.getUsers());
    setLogs(await AUTH.getAuditLogs());
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ email: "", password: "", first_name: "", last_name: "", role: "agent", status: "active" }); setEditId(null); setModal(true); };
  const openEdit = (u) => { setForm({ email: u.email, first_name: u.first_name, last_name: u.last_name, role: u.role, status: u.status, password: "" }); setEditId(u.id); setModal(true); };

  const handleSave = async () => {
    if (!form.email || (!editId && !form.password)) { setToast({ message: "Remplissez tous les champs", type: "error" }); return; }
    if (!editId && form.password.length < 8) { setToast({ message: "Mot de passe : 8 caractères minimum", type: "error" }); return; }
    const existing = users.find(u => u.email === form.email.toLowerCase() && u.id !== editId);
    if (existing) { setToast({ message: "Cet email existe déjà", type: "error" }); return; }

    if (editId) {
      const update = { email: form.email, first_name: form.first_name, last_name: form.last_name, role: form.role, status: form.status };
      if (form.password) update.password = form.password;
      await AUTH.updateUser(editId, update);
      await API.insert("audit_logs", { company_id: COMPANY_ID, user_id: currentUser.id, action: "user_update", details: `Modification utilisateur : ${form.email}` });
    } else {
      await AUTH.createUser({ ...form, avatar: (form.first_name?.[0] || "") + (form.last_name?.[0] || "") });
      await API.insert("audit_logs", { company_id: COMPANY_ID, user_id: currentUser.id, action: "user_create", details: `Création utilisateur : ${form.email} (${form.role})` });
    }
    setModal(false); setToast({ message: editId ? "Utilisateur modifié" : "Utilisateur créé", type: "success" }); load();
  };

  const deleteUser = async (u) => {
    if (u.id === currentUser.id) { setToast({ message: "Impossible de supprimer votre propre compte", type: "error" }); return; }
    await API.remove("auth_users", u.id);
    await API.insert("audit_logs", { company_id: COMPANY_ID, user_id: currentUser.id, action: "user_delete", details: `Suppression : ${u.email}` });
    setToast({ message: "Utilisateur supprimé", type: "success" }); load();
  };

  const roleInfo = AUTH.ROLES;

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, background: T.surfaceAlt, borderRadius: 12, padding: 4, border: `1px solid ${T.border}`, width: "fit-content" }}>
        {[["users", "Utilisateurs", Users], ["audit", "Journal d'audit", Shield], ["security", "Sécurité", Lock]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 12, fontWeight: tab === id ? 600 : 500, background: tab === id ? T.primary : "transparent", color: tab === id ? "#fff" : T.textMuted }}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 12 }}>
              <KPI icon={Users} label="Total" value={users.length.toString()} color={T.primary} glow={T.primaryGlow} />
              <KPI icon={Shield} label="Admins" value={users.filter(u => u.role === "super_admin" || u.role === "admin").length.toString()} color="#C9A84C" glow="rgba(201,168,76,0.15)" />
              <KPI icon={Activity} label="Actifs" value={users.filter(u => u.status === "active").length.toString()} color={T.accent} glow={T.accentGlow} />
            </div>
            <Btn onClick={openNew} icon={Plus}>Nouvel Utilisateur</Btn>
          </div>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Utilisateur", "Email", "Rôle", "Statut", "Dernière connexion", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {users.map(u => {
                  const r = roleInfo[u.role] || roleInfo.viewer;
                  return (
                    <tr key={u.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: `${r.color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: r.color }}>{u.avatar || "?"}</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{u.first_name} {u.last_name}</div>
                            {u.id === currentUser.id && <span style={{ fontSize: 10, color: "#C9A84C" }}>(vous)</span>}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: T.textMuted }}>{u.email}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ background: `${r.color}22`, color: r.color, padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{r.label}</span>
                      </td>
                      <td style={{ padding: "12px 16px" }}><StatusBadge status={u.status} /></td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: T.textDim }}>{u.last_login ? new Date(u.last_login).toLocaleString("fr-FR") : "Jamais"}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => openEdit(u)} style={{ background: T.surfaceAlt, border: "none", borderRadius: 6, padding: 6, cursor: "pointer" }}><Pencil size={14} color={T.textMuted} /></button>
                          {u.id !== currentUser.id && <button onClick={() => deleteUser(u)} style={{ background: T.surfaceAlt, border: "none", borderRadius: 6, padding: 6, cursor: "pointer" }}><Trash2 size={14} color={T.danger} /></button>}
                          {u.locked_until && new Date(u.locked_until) > new Date() && (
                            <button onClick={async () => { await AUTH.updateUser(u.id, { locked_until: null, login_attempts: 0 }); setToast({ message: "Compte déverrouillé", type: "success" }); load(); }} style={{ background: "rgba(245,158,11,0.1)", border: "none", borderRadius: 6, padding: 6, cursor: "pointer" }} title="Déverrouiller"><Lock size={14} color={T.warning} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Modifier Utilisateur" : "Nouvel Utilisateur"} width={500}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <Input label="Prénom *" value={form.first_name || ""} onChange={v => setForm({ ...form, first_name: v })} />
              <Input label="Nom *" value={form.last_name || ""} onChange={v => setForm({ ...form, last_name: v })} />
            </div>
            <Input label="Email *" value={form.email || ""} onChange={v => setForm({ ...form, email: v })} type="email" icon={Mail} />
            <Input label={editId ? "Nouveau mot de passe (vide = inchangé)" : "Mot de passe *"} value={form.password || ""} onChange={v => setForm({ ...form, password: v })} type="password" icon={Lock} />
            {!editId && <div style={{ fontSize: 11, color: T.textDim, marginTop: -10, marginBottom: 10 }}>Minimum 8 caractères</div>}
            <Select label="Rôle *" value={form.role || "agent"} onChange={v => setForm({ ...form, role: v })} options={Object.entries(roleInfo).map(([k, v]) => ({ value: k, label: v.label }))} />
            <Select label="Statut" value={form.status || "active"} onChange={v => setForm({ ...form, status: v })} options={[{ value: "active", label: "Actif" }, { value: "suspended", label: "Suspendu" }, { value: "inactive", label: "Inactif" }]} />
            {form.role && (
              <div style={{ background: T.surfaceAlt, borderRadius: 10, padding: "10px 14px", marginTop: 4, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 6 }}>Accès autorisés :</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {(roleInfo[form.role]?.pages || []).map(p => (
                    <span key={p} style={{ background: T.card, borderRadius: 4, padding: "2px 8px", fontSize: 10, color: T.textMuted, border: `1px solid ${T.border}` }}>{p}</span>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
              <Btn variant="secondary" onClick={() => setModal(false)}>Annuler</Btn>
              <Btn onClick={handleSave} icon={Save}>{editId ? "Modifier" : "Créer"}</Btn>
            </div>
          </Modal>
        </div>
      )}

      {tab === "audit" && (
        <SectionCard title="Journal d'Audit" description="Historique de toutes les actions sur la plateforme">
          {logs.length === 0 ? <div style={{ color: T.textMuted, fontSize: 13 }}>Aucun log</div> : (
            <div style={{ maxHeight: 500, overflow: "auto" }}>
              {logs.slice(0, 50).map(l => (
                <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, background: l.action === "login" ? T.accent : l.action === "logout" ? T.textDim : l.action?.includes("delete") ? T.danger : T.primary }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: T.text }}>{l.details}</div>
                    <div style={{ fontSize: 11, color: T.textDim }}>{l.action} • {l.ip}</div>
                  </div>
                  <div style={{ fontSize: 11, color: T.textDim, whiteSpace: "nowrap" }}>{l.created_at ? new Date(l.created_at).toLocaleString("fr-FR") : ""}</div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {tab === "security" && (
        <SectionCard title="Politique de Sécurité" description="Configuration des règles de sécurité de la plateforme">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 5, fontWeight: 500 }}>Tentatives max avant verrouillage</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.warning }}>5</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 5, fontWeight: 500 }}>Durée verrouillage</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.danger }}>15 min</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 5, fontWeight: 500 }}>Durée session</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.accent }}>24 heures</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 5, fontWeight: 500 }}>Mot de passe minimum</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.primary }}>8 caract.</div>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>Rôles et permissions</div>
            {Object.entries(roleInfo).map(([k, v]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
                <span style={{ background: `${v.color}22`, color: v.color, padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, minWidth: 100, textAlign: "center" }}>{v.label}</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {v.pages.map(p => (
                    <span key={p} style={{ background: T.surfaceAlt, borderRadius: 4, padding: "2px 8px", fontSize: 10, color: T.textMuted, border: `1px solid ${T.border}` }}>{p}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   LOGIN PAGE — Split screen, no logo, text only
   ═══════════════════════════════════════════════════════════ */
const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async () => {
    if (!email || !password) { setError("Veuillez remplir tous les champs"); return; }
    setLoading(true); setError("");
    const result = await AUTH.login(email, password);
    setLoading(false);
    if (result.ok) { onLogin(result.user); }
    else { setError(result.error); }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSubmit(); };

  return (
    <div style={{ display: "flex", height: "100vh", background: T.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif", overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}} @keyframes pulse{0%,100%{opacity:0.4}50%{opacity:0.8}} input::placeholder{color:#4A5568} @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} @media(max-width:768px){.hs-login-left{display:none!important}.hs-login-right{width:100%!important;border:none!important}}`}</style>

      {/* Left — Branding (hidden on mobile) */}
      <div className="hs-login-left" style={{ flex: 1, background: "linear-gradient(135deg, #0B0F1A 0%, #111827 40%, #1A1A2E 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "10%", left: "15%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%)", animation: "pulse 4s ease infinite" }} />
        <div style={{ position: "absolute", bottom: "15%", right: "10%", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,168,76,0.04) 0%, transparent 70%)", animation: "pulse 6s ease infinite" }} />
        <div style={{ position: "absolute", inset: 0, background: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A84C' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />

        <div style={{ position: "relative", zIndex: 1, textAlign: "center", animation: "fadeIn 0.8s ease" }}>
          <div style={{ fontSize: 42, fontWeight: 800, color: "#C9A84C", letterSpacing: 3 }}>H&S IMMO</div>
          <div style={{ fontSize: 13, color: "#7B8BA6", letterSpacing: 5, textTransform: "uppercase", marginTop: 10 }}>Real Estate Management</div>
          <div style={{ width: 60, height: 2, background: "linear-gradient(90deg, transparent, #C9A84C, transparent)", margin: "24px auto" }} />
          <div style={{ fontSize: 14, color: "#4A5568", maxWidth: 320, lineHeight: 1.7 }}>
            Plateforme de gestion immobilière complète<br />— Location, CRM, Finances, Contrats
          </div>
        </div>

        <div style={{ position: "absolute", bottom: 24, fontSize: 11, color: "#2A3A5C" }}>© 2026 H&S Immo. Tous droits réservés.</div>
      </div>

      {/* Right — Login Form */}
      <div className="hs-login-right" style={{ width: 440, background: T.surface, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 48px", borderLeft: `1px solid ${T.border}`, boxSizing: "border-box" }}>
        <div style={{ animation: "fadeIn 0.6s ease 0.2s both" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 6 }}>Connexion</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 32 }}>Accédez à votre espace H&S Immo</div>

          {error && (
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 12, padding: "10px 14px", marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
              <AlertOctagon size={16} color={T.danger} />
              <span style={{ fontSize: 12, color: T.danger }}>{error}</span>
            </div>
          )}

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 6, fontWeight: 500 }}>Email</div>
            <div style={{ position: "relative" }}>
              <Mail size={16} color={T.textDim} style={{ position: "absolute", left: 14, top: 13 }} />
              <input value={email} onChange={e => setEmail(e.target.value)} onKeyDown={handleKeyDown} type="email" placeholder="votre@email.com" autoComplete="email"
                style={{ width: "100%", background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 14px 12px 42px", color: T.text, fontSize: 14, outline: "none", boxSizing: "border-box", transition: "border 0.2s" }}
                onFocus={e => e.target.style.borderColor = "#C9A84C"} onBlur={e => e.target.style.borderColor = T.border} />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 6, fontWeight: 500 }}>Mot de passe</div>
            <div style={{ position: "relative" }}>
              <Lock size={16} color={T.textDim} style={{ position: "absolute", left: 14, top: 13 }} />
              <input value={password} onChange={e => setPassword(e.target.value)} onKeyDown={handleKeyDown} type={showPwd ? "text" : "password"} placeholder="••••••••" autoComplete="current-password"
                style={{ width: "100%", background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 44px 12px 42px", color: T.text, fontSize: 14, outline: "none", boxSizing: "border-box", transition: "border 0.2s" }}
                onFocus={e => e.target.style.borderColor = "#C9A84C"} onBlur={e => e.target.style.borderColor = T.border} />
              <button onClick={() => setShowPwd(!showPwd)} style={{ position: "absolute", right: 12, top: 11, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                <Eye size={16} color={T.textDim} />
              </button>
            </div>
          </div>

          <button onClick={handleSubmit} disabled={loading}
            style={{ width: "100%", padding: "13px 0", borderRadius: 12, border: "none", background: loading ? T.surfaceAlt : "linear-gradient(135deg, #C9A84C, #8B6914)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer", transition: "all 0.2s", letterSpacing: 0.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxSizing: "border-box" }}>
            {loading ? <><Loader size={16} style={{ animation: "spin 1s linear infinite" }} /> Connexion...</> : <><Lock size={16} /> Se connecter</>}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 20, justifyContent: "center" }}>
            <Shield size={14} color={T.textDim} />
            <span style={{ fontSize: 11, color: T.textDim }}>Connexion sécurisée • Chiffrement SSL • Verrouillage automatique</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   NAV & MAIN APP — With Auth
   ═══════════════════════════════════════════════════════════ */
const ALL_NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "properties", label: "Propriétés", icon: Building2 },
  { id: "clients", label: "Clients", icon: Users },
  { id: "bookings", label: "Réservations", icon: CalendarDays },
  { id: "contracts", label: "Contrats", icon: FileText },
  { id: "finance", label: "Finances", icon: DollarSign },
  { id: "messages", label: "Messages", icon: MessageCircle },
  { id: "social", label: "Réseaux Sociaux", icon: Share2 },
  { id: "users", label: "Utilisateurs", icon: Shield },
  { id: "settings", label: "Paramètres", icon: Settings },
];

const ALL_PAGES = { dashboard: DashboardPage, properties: PropertiesPage, clients: ClientsPage, bookings: BookingsPage, contracts: ContractsPage, finance: FinancePage, messages: MessagesPage, social: SocialPage, settings: SettingsPage };

export default function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [page, setPage] = useState("dashboard");
  const [collapsed, setCollapsed] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  // Init auth system
  useEffect(() => {
    (async () => {
      await AUTH.init();
      // Check saved session
      try {
        const sess = await window.storage.get("current_session");
        if (sess?.value) {
          const u = JSON.parse(sess.value);
          setUser(u);
        }
      } catch {}
      setAuthReady(true);
    })();
  }, []);

  const handleLogin = async (u) => {
    setUser(u);
    try { await window.storage.set("current_session", JSON.stringify(u)); } catch {}
  };

  const handleLogout = async () => {
    if (user) await AUTH.logout(user.id);
    setUser(null);
    setShowLogout(false);
    setPage("dashboard");
    try { await window.storage.delete("current_session"); } catch {}
  };

  if (!authReady) return (
    <div style={{ display: "flex", height: "100vh", background: T.bg, alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif" }}>
      <Loader size={24} color="#C9A84C" style={{ animation: "spin 1s linear infinite" }} />
    </div>
  );

  if (!user) return <LoginPage onLogin={handleLogin} />;

  // Role-based navigation
  const rolePages = AUTH.ROLES[user.role]?.pages || ["dashboard"];
  const NAV = ALL_NAV.filter(n => rolePages.includes(n.id));
  const PageComponent = page === "users" && rolePages.includes("users")
    ? () => <UsersPage currentUser={user} />
    : ALL_PAGES[page] || DashboardPage;
  const nav = ALL_NAV.find(n => n.id === page);

  return (
    <div style={{ display: "flex", height: "100vh", background: T.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif", color: T.text, overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} @keyframes fadeIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}} @keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}} *::-webkit-scrollbar{width:6px} *::-webkit-scrollbar-track{background:transparent} *::-webkit-scrollbar-thumb{background:${T.border};border-radius:3px}
@media(max-width:768px){
  .hs-sidebar-closed{display:none!important}
  .hs-main{width:100%!important}
  .hs-header{padding:0 12px!important}
  .hs-content{padding:12px!important}
  .hs-kpi-row{flex-direction:column!important}
  .hs-kpi-row>div{min-width:unset!important}
  .hs-grid-2{grid-template-columns:1fr!important}
  .hs-grid-3{grid-template-columns:1fr!important}
  .hs-grid-cards{grid-template-columns:1fr!important}
  .hs-table-wrap{overflow-x:auto}
  .hs-table-wrap table{min-width:600px}
  .hs-modal-inner{width:95vw!important;max-height:90vh!important;margin:16px}
  .hs-settings-grid{grid-template-columns:1fr!important}
  .hs-settings-nav{display:none!important}
  .hs-hamburger{display:flex!important}
  .hs-mobile-overlay{display:block!important}
}`}</style>

      {/* Mobile menu overlay */}
      {mobileMenu && <div onClick={() => setMobileMenu(false)} className="hs-mobile-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 90 }} />}

      {/* Sidebar — hidden on mobile when closed, visible when mobileMenu open */}
      <div className={mobileMenu ? "hs-sidebar-open" : "hs-sidebar-closed"} style={{ width: mobileMenu ? 240 : collapsed ? 68 : 240, background: T.surface, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", transition: "width 0.2s", flexShrink: 0, overflow: "hidden", ...(mobileMenu ? { position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 95 } : {}) }}>
        <div onClick={() => { setPage("dashboard"); setMobileMenu(false); }} style={{ padding: collapsed && !mobileMenu ? "16px 12px" : "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, minHeight: 56, cursor: "pointer" }}>
          <svg width="32" height="32" viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
            <defs><linearGradient id="hsGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#C9A84C" /><stop offset="100%" stopColor="#8B6914" /></linearGradient></defs>
            <rect width="32" height="32" rx="8" fill="#111827" />
            <rect x="1" y="1" width="30" height="30" rx="7" fill="none" stroke="url(#hsGrad)" strokeWidth="1.5" />
            <text x="16" y="13" textAnchor="middle" fill="url(#hsGrad)" fontSize="9" fontWeight="800" fontFamily="Arial">H&amp;S</text>
            <rect x="6" y="16" width="20" height="1" fill="#C9A84C" opacity="0.4" />
            <path d="M10 22 L16 19 L22 22 L22 27 L10 27 Z" fill="none" stroke="url(#hsGrad)" strokeWidth="1.2" />
            <rect x="14" y="23" width="4" height="4" fill="url(#hsGrad)" opacity="0.6" />
          </svg>
          {(mobileMenu || !collapsed) && <div style={{ display: "flex", flexDirection: "column" }}><span style={{ fontSize: 15, fontWeight: 700, color: "#C9A84C", whiteSpace: "nowrap", letterSpacing: "0.5px" }}>H&S Immo</span><span style={{ fontSize: 9, color: T.textDim, letterSpacing: "1px", textTransform: "uppercase" }}>Real Estate</span></div>}
        </div>
        <nav style={{ flex: 1, padding: "8px", overflow: "auto" }}>
          {NAV.map(n => (
            <button key={n.id} onClick={() => { setPage(n.id); setMobileMenu(false); }} style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%", padding: collapsed && !mobileMenu ? "10px 0" : "10px 12px",
              justifyContent: collapsed && !mobileMenu ? "center" : "flex-start", borderRadius: 10, border: "none", cursor: "pointer", marginBottom: 2,
              background: page === n.id ? T.primaryGlow : "transparent", color: page === n.id ? T.primary : T.textMuted, transition: "all 0.15s"
            }}>
              <n.icon size={19} />{(mobileMenu || !collapsed) && <span style={{ fontSize: 13, fontWeight: page === n.id ? 600 : 500, whiteSpace: "nowrap" }}>{n.label}</span>}
            </button>
          ))}
        </nav>

        {/* User info in sidebar */}
        <div style={{ padding: collapsed && !mobileMenu ? "8px" : "8px 12px", borderTop: `1px solid ${T.border}` }}>
          {(mobileMenu || !collapsed) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 8px", marginBottom: 4 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: `${AUTH.ROLES[user.role]?.color || T.primary}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: AUTH.ROLES[user.role]?.color || T.primary }}>{user.avatar || "?"}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.first_name} {user.last_name}</div>
                <div style={{ fontSize: 10, color: AUTH.ROLES[user.role]?.color || T.textDim }}>{AUTH.ROLES[user.role]?.label || user.role}</div>
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 4 }}>
            {!mobileMenu && <button onClick={() => setCollapsed(!collapsed)} style={{ flex: 1, padding: 10, borderRadius: 10, border: `1px solid ${T.border}`, background: T.surfaceAlt, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>}
            {(mobileMenu || !collapsed) && (
              <button onClick={() => { handleLogout(); setMobileMenu(false); }} style={{ flex: mobileMenu ? 1 : undefined, padding: "10px 12px", borderRadius: 10, border: `1px solid rgba(239,68,68,0.3)`, background: "rgba(239,68,68,0.1)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: T.danger, fontSize: 12, fontWeight: 500 }}>
                <X size={14} /> Déconnexion
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="hs-main" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <header className="hs-header" style={{ height: 56, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Mobile hamburger */}
            <button onClick={() => setMobileMenu(true)} className="hs-hamburger" style={{ display: "none", background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 8, padding: 7, cursor: "pointer", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" viewBox="0 0 18 18"><path d="M2 4h14M2 9h14M2 14h14" stroke={T.textMuted} strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{nav?.label || "Dashboard"}</div>
            <span style={{ background: `${AUTH.ROLES[user.role]?.color || T.primary}22`, color: AUTH.ROLES[user.role]?.color || T.primary, padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600 }}>{AUTH.ROLES[user.role]?.label}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => setNotifOpen(!notifOpen)} style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 10, padding: 8, cursor: "pointer", position: "relative" }}>
              <Bell size={18} color={T.textMuted} />
            </button>
            <div onClick={() => setShowLogout(!showLogout)} style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#C9A84C,#8B6914)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", position: "relative" }}>
              {user.avatar || "HS"}
              {showLogout && (
                <div style={{ position: "absolute", top: 42, right: 0, background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.4)", zIndex: 100, overflow: "hidden", width: 200 }}>
                  <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{user.first_name} {user.last_name}</div>
                    <div style={{ fontSize: 11, color: T.textDim }}>{user.email}</div>
                  </div>
                  <button onClick={handleLogout} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 16px", border: "none", background: "transparent", cursor: "pointer", color: T.danger, fontSize: 13 }}>
                    <X size={14} /> Déconnexion
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="hs-content" style={{ flex: 1, overflow: "auto", padding: 24 }} onClick={() => { notifOpen && setNotifOpen(false); showLogout && setShowLogout(false); }}>
          <PageComponent setPage={setPage} currentUser={user} />
        </main>
      </div>
    </div>
  );
}
